import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, PieChart, Pie, Cell } from 'recharts';
import { 
  Users, 
  Zap, 
  BarChart3, 
  UtensilsCrossed, 
  UserPlus, 
  Calendar,
  TrendingUp,
  Clock,
  FileText,
  Settings as SettingsIcon,
  CreditCard,
  LogOut,
  Menu,
  X,
  ChevronRight,
  ChevronLeft,
  ArrowLeft,
  Plus,
  Search,
  Filter,
  MoreVertical,
  Edit,
  Trash2,
  Download,
  Eye,
  Scale,
  Target,
  Activity,
  Sparkles,
  Crown,
  Shield,
  LayoutDashboard,
  RefreshCw,
  Flame,
  Brain,
  Check,
  XCircle,
  AlertTriangle,
  PartyPopper,
  ClipboardList,
  Mail,
  Lock,
  Copy,
  Gift,
  Palette,
  User,
  Lightbulb,
  DollarSign,
  CalendarDays,
  ShoppingCart,
  Calculator,
  FileDown,
  HelpCircle,
  Bug,
  MessageSquare
} from 'lucide-react';
import './App.css';

// Import page components
import { AuthPage, BillingPage, SettingsPage, AdminDashboard, HelpPage } from './pages';
import BugReportModal from './components/BugReportModal';
import Navbar from './components/Navbar';

// API URL - Always use the environment variable set at build time
// For Emergent deployments, REACT_APP_BACKEND_URL is automatically configured
// For custom domains, ensure REACT_APP_BACKEND_URL points to your API
const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
console.log('API URL configured as:', API_URL);

// Animated counter hook for wow-factor number animations
function useAnimatedCounter(end, duration = 1000, startOnMount = true) {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  
  useEffect(() => {
    if (!startOnMount || hasAnimated || end === 0) return;
    
    let startTime;
    let animationFrame;
    
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      
      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(easeOutQuart * end));
      
      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      } else {
        setHasAnimated(true);
      }
    };
    
    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [end, duration, startOnMount, hasAnimated]);
  
  return count;
}

// Animated counter component for displaying numbers with animation
function AnimatedCounter({ value, duration = 1000 }) {
  const animatedValue = useAnimatedCounter(value, duration);
  return <p className="client-count animated-number">{animatedValue}</p>;
}

// Configure axios with timeout (no credentials needed - using Authorization header)
axios.defaults.timeout = 30000; // 30 second timeout

// Add auth token to requests (fallback for mobile browsers that block cookies)
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
    // Debug: log when we're adding auth header
    if (config.url?.includes('/api/clients') || config.url?.includes('/api/meal-plans')) {
      console.log('Axios request to:', config.url, '- Auth token attached:', token.substring(0, 15) + '...');
    }
  } else {
    console.warn('Axios request to:', config.url, '- NO AUTH TOKEN in localStorage!');
  }
  return config;
});

// Handle auth token from responses
axios.interceptors.response.use((response) => {
  // If server returns a token, store it
  if (response.data?.auth_token) {
    localStorage.setItem('auth_token', response.data.auth_token);
  }
  return response;
}, (error) => {
  // On 401, clear stored token
  if (error.response?.status === 401) {
    localStorage.removeItem('auth_token');
  }
  return Promise.reject(error);
});

// Helper function to parse API error responses
function parseErrorMessage(err, fallback = 'An error occurred') {
  // Network error (no response from server)
  if (err?.message === 'Network Error' || !err?.response) {
    return 'Unable to connect to server. Please check your internet connection and try again.';
  }
  
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map(e => {
      const field = e.loc?.slice(-1)[0] || 'field';
      return `${field}: ${e.msg}`;
    }).join(', ');
  }
  if (typeof detail === 'object') return detail.msg || detail.message || JSON.stringify(detail);
  return fallback;
}

// Macro Pie Chart Component
const MACRO_COLORS = {
  protein: '#ef4444',    // Red
  carbs: '#3b82f6',      // Blue
  fat: '#f59e0b'         // Amber
};

const MacroPieChart = ({ protein, carbs, fat, proteinGrams, carbsGrams, fatGrams, calories, size = 120, showLabels = true, showLegend = true }) => {
  const data = [
    { name: 'Protein', value: protein, color: MACRO_COLORS.protein },
    { name: 'Carbs', value: carbs, color: MACRO_COLORS.carbs },
    { name: 'Fat', value: fat, color: MACRO_COLORS.fat }
  ];
  
  const total = protein + carbs + fat;
  const pGrams = proteinGrams || (calories ? Math.round(protein / 100 * calories / 4) : null);
  const cGrams = carbsGrams || (calories ? Math.round(carbs / 100 * calories / 4) : null);
  const fGrams = fatGrams || (calories ? Math.round(fat / 100 * calories / 9) : null);
  
  return (
    <div className="macro-pie-container">
      <PieChart width={size} height={size}>
        <Pie
          data={data}
          cx={size / 2}
          cy={size / 2}
          innerRadius={size * 0.3}
          outerRadius={size * 0.45}
          paddingAngle={2}
          dataKey="value"
          stroke="none"
        >
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={entry.color} />
          ))}
        </Pie>
        {showLabels && (
          <text
            x={size / 2}
            y={size / 2}
            textAnchor="middle"
            dominantBaseline="middle"
            className="pie-center-text"
          >
            <tspan x={size / 2} dy="-0.3em" fontSize="14" fontWeight="700" fill="#111827">{total}</tspan>
            <tspan x={size / 2} dy="1.3em" fontSize="10" fill="#6b7280">total %</tspan>
          </text>
        )}
      </PieChart>
      {showLegend && (
        <div className="macro-legend">
          <div className="legend-item">
            <span className="legend-dot" style={{background: MACRO_COLORS.protein}}></span>
            <span className="legend-label">Protein</span>
            <span className="legend-value">{pGrams ? `${pGrams}g (${protein}%)` : `${protein}%`}</span>
          </div>
          <div className="legend-item">
            <span className="legend-dot" style={{background: MACRO_COLORS.carbs}}></span>
            <span className="legend-label">Carbs</span>
            <span className="legend-value">{cGrams ? `${cGrams}g (${carbs}%)` : `${carbs}%`}</span>
          </div>
          <div className="legend-item">
            <span className="legend-dot" style={{background: MACRO_COLORS.fat}}></span>
            <span className="legend-label">Fat</span>
            <span className="legend-value">{fGrams ? `${fGrams}g (${fat}%)` : `${fat}%`}</span>
          </div>
        </div>
      )}
    </div>
  );
};

function App() {
  // Auth state
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [authView, setAuthView] = useState('login'); // 'login', 'register'
  
  // Ref to prevent double verification in StrictMode
  const verificationAttempted = useRef(false);
  
  // App state
  const [currentView, setCurrentView] = useState('home');
  const [clients, setClients] = useState([]);
  const [clientsCount, setClientsCount] = useState(0);
  const [selectedClient, setSelectedClient] = useState(null);
  const [mealPlans, setMealPlans] = useState([]);
  const [selectedMealPlan, setSelectedMealPlan] = useState(null);
  const [recentMealPlans, setRecentMealPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [swappingMeal, setSwappingMeal] = useState(null);
  const [customMealModal, setCustomMealModal] = useState(null); // { sampleKey, mealType }
  const [customMealPrompt, setCustomMealPrompt] = useState('');
  const [submittingCustomMeal, setSubmittingCustomMeal] = useState(false);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [pdfPreviewPlan, setPdfPreviewPlan] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Subscription state
  const [subscription, setSubscription] = useState(null);
  
  // Helper function to check feature availability based on plan
  const isFeatureAvailable = (feature) => {
    if (!subscription) return false;
    const plan = subscription.plan;
    const planStatus = subscription.plan_status;
    
    // If expired, treat as starter
    if (planStatus === 'expired') {
      return false;
    }
    
    const featuresByPlan = {
      starter: {
        custom_branding: false,
        calorie_cycling: false,
        shopping_list: false,
        unlimited_swaps: false,
        white_label_pdf: false,
        meal_templates: false,
        quick_meal_plan: false,
        team_members: false
      },
      pro: {
        custom_branding: true,
        calorie_cycling: true,
        shopping_list: true,
        unlimited_swaps: true,
        white_label_pdf: true,
        meal_templates: false,
        quick_meal_plan: true,
        team_members: false
      },
      unlimited: {
        custom_branding: true,
        calorie_cycling: true,
        shopping_list: true,
        unlimited_swaps: true,
        white_label_pdf: true,
        meal_templates: true,
        quick_meal_plan: true,
        team_members: true
      }
    };
    
    return featuresByPlan[plan]?.[feature] ?? false;
  };

  // Check if user is admin
  const isAdmin = () => {
    return user?.is_admin === true;
  };
  
  // Form states
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [registerForm, setRegisterForm] = useState({ name: '', email: '', password: '', confirmPassword: '' });
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [resetPasswordForm, setResetPasswordForm] = useState({ token: '', newPassword: '', confirmPassword: '' });
  const [editingClient, setEditingClient] = useState(false);
  const [editFormData, setEditFormData] = useState(null);
  
  const [mealPlanOptions, setMealPlanOptions] = useState({
    template_option: 1,
    calorie_cycling: false,
    high_calorie_days: []
  });
  
  // Settings state
  const [brandingForm, setBrandingForm] = useState({ 
    primary_color: '#021b79', 
    secondary_color: '#ffffff',
    company_name: ''
  });
  const [uploadingLogo, setUploadingLogo] = useState(false);
  
  // Quick Meal Plan state
  const [quickMealData, setQuickMealData] = useState({
    calories: '',
    num_meals: 3,
    protein_pct: '30',
    carbs_pct: '40',
    fat_pct: '30',
    dietary_restrictions: []
  });
  const [quickMealResult, setQuickMealResult] = useState(null);
  
  // Custom Meal Builder state
  const [showMealEditor, setShowMealEditor] = useState(false);
  const [editingMealData, setEditingMealData] = useState({
    sample_key: '',
    meal_type: '',
    meal: {
      name: '',
      calories: 0,
      protein: 0,
      carbs: 0,
      fat: 0,
      ingredients: [],
      instructions: ''
    }
  });
  const [savingMeal, setSavingMeal] = useState(false);
  
  // Confirmation modal state
  const [confirmModal, setConfirmModal] = useState({
    show: false,
    title: '',
    message: '',
    onConfirm: null
  });
  
  // Meal generation progress state
  const [generatingMealPlan, setGeneratingMealPlan] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [generationStep, setGenerationStep] = useState('');
  
  // Duplicate meal plan modal state
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateTargetClient, setDuplicateTargetClient] = useState('');
  
  // Weight tracking state
  const [weightHistory, setWeightHistory] = useState([]);
  const [showAddWeight, setShowAddWeight] = useState(false);
  const [newWeight, setNewWeight] = useState({ weight: '', note: '' });

  // Templates state (Unlimited plan)
  const [templates, setTemplates] = useState([]);
  const [templateCategories, setTemplateCategories] = useState([]);
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
  const [newTemplateForm, setNewTemplateForm] = useState({ name: '', category: 'General', description: '' });
  const [selectedTemplateCategory, setSelectedTemplateCategory] = useState('');
  const [showApplyTemplateModal, setShowApplyTemplateModal] = useState(false);
  const [selectedTemplateToApply, setSelectedTemplateToApply] = useState(null);

  // Team state (Unlimited plan)
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamStatus, setTeamStatus] = useState(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', name: '', password: '' });
  const [showBugReportModal, setShowBugReportModal] = useState(false);

  // Admin state
  const [adminStats, setAdminStats] = useState(null);
  const [adminUsers, setAdminUsers] = useState([]);
  const [adminPagination, setAdminPagination] = useState({ page: 1, limit: 20, total: 0, total_pages: 0 });
  const [adminFilters, setAdminFilters] = useState({ plan: '', status: '', search: '' });
  const [selectedAdminUser, setSelectedAdminUser] = useState(null);
  const [adminLoading, setAdminLoading] = useState(false);

  // Form state for new clients
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    age: '',
    gender: 'male',
    height: '',
    weight: '',
    goal_weight: '',
    activity_level: 'moderately_active',
    dietary_restrictions: [],
    num_meals: 3,
    num_snacks: 2,
    allergies: [],
    food_preferences: '',
    manual_calories: '',
    manual_protein_pct: '',
    manual_carbs_pct: '',
    manual_fat_pct: '',
    manual_protein_grams: '',
    manual_carbs_grams: '',
    manual_fat_grams: '',
    bmr_equation: 'harris_benedict',
    body_fat_pct: '',
    protein_per_kg: ''
  });

  // Check auth on mount and handle OAuth callback
  useEffect(() => {
    const checkAuth = async () => {
      // Check for reset token (password reset)
      const urlParams = new URLSearchParams(window.location.search);
      const resetToken = urlParams.get('reset_token');
      const verifyEmailToken = urlParams.get('verify_email');
      
      if (resetToken) {
        // Verify the reset token
        try {
          const verifyResponse = await axios.get(`${API_URL}/api/auth/verify-reset-token/${resetToken}`);
          if (verifyResponse.data.valid) {
            setResetPasswordForm(prev => ({ ...prev, token: resetToken }));
            setAuthView('reset-password');
            setSuccess(`Reset password for ${verifyResponse.data.email}`);
          }
        } catch (err) {
          setError(err.response?.data?.detail || 'Invalid or expired reset link');
          setAuthView('login');
        }
        setAuthLoading(false);
        return;
      }
      
      // Handle email verification
      if (verifyEmailToken) {
        // Prevent double verification in React StrictMode
        if (verificationAttempted.current) {
          setAuthLoading(false);
          return;
        }
        verificationAttempted.current = true;
        
        try {
          const verifyResponse = await axios.get(`${API_URL}/api/auth/verify-email/${verifyEmailToken}`);
          if (verifyResponse.data.success) {
            setError(null); // Clear any previous error
            setSuccess(verifyResponse.data.message);
            // Clean URL
            window.history.replaceState({}, document.title, window.location.pathname);
            // Try to get current user session
            try {
              const userResponse = await axios.get(`${API_URL}/api/auth/me`);
              setUser(userResponse.data);
            } catch {
              // Not logged in - show login
              setAuthView('login');
            }
          }
        } catch (err) {
          setSuccess(null); // Clear any previous success
          setError(err.response?.data?.detail || 'Invalid or expired verification link');
          // Clean URL
          window.history.replaceState({}, document.title, window.location.pathname);
        }
        setAuthLoading(false);
        return;
      }
      
      // Check for Stripe callback (query params)
      const stripeSessionId = urlParams.get('session_id');
      const stripeSuccess = urlParams.get('success');
      const stripeCanceled = urlParams.get('canceled');
      
      // Handle Stripe checkout callback (has success or canceled param)
      if (stripeSessionId && (stripeSuccess || stripeCanceled)) {
        console.log('Processing Stripe callback with session_id:', stripeSessionId.substring(0, 20) + '...');
        
        // Clean URL params immediately to prevent re-triggering
        const cleanUrl = window.location.origin + window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);
        
        if (stripeSuccess) {
          // Verify payment and auto-login
          try {
            const verifyResponse = await axios.post(`${API_URL}/api/billing/verify-payment?session_id=${stripeSessionId}`);
            console.log('Payment verification response:', verifyResponse.data);
            
            if (verifyResponse.data.success && verifyResponse.data.user) {
              setUser(verifyResponse.data.user);
              setSuccess(verifyResponse.data.message || 'Payment successful!');
              
              // Load subscription with retry polling
              const pollSubscription = async (retries = 5) => {
                for (let i = 0; i < retries; i++) {
                  try {
                    const subResponse = await axios.get(`${API_URL}/api/billing/subscription`);
                    if (subResponse.data && subResponse.data.plan_status === 'active') {
                      setSubscription(subResponse.data);
                      console.log('Subscription confirmed active:', subResponse.data);
                      return;
                    }
                  } catch (subErr) {
                    console.error(`Subscription poll attempt ${i + 1} failed:`, subErr);
                  }
                  await new Promise(r => setTimeout(r, 1500));
                }
                // Final attempt even if not active
                try {
                  const subResponse = await axios.get(`${API_URL}/api/billing/subscription`);
                  setSubscription(subResponse.data);
                } catch {}
              };
              await pollSubscription();
              
              setCurrentView('billing');
            } else {
              // Payment still processing - try to get user session
              try {
                const userResponse = await axios.get(`${API_URL}/api/auth/me`);
                setUser(userResponse.data);
                setSuccess('Payment is being processed. Your subscription will be updated shortly.');
                setCurrentView('billing');
                // Still try to poll subscription
                setTimeout(async () => {
                  try {
                    const subResponse = await axios.get(`${API_URL}/api/billing/subscription`);
                    setSubscription(subResponse.data);
                  } catch {}
                }, 3000);
              } catch {
                setError('Please log in to view your subscription status.');
              }
            }
          } catch (err) {
            console.error('Payment verification error:', err);
            // Fallback - try existing session
            try {
              const userResponse = await axios.get(`${API_URL}/api/auth/me`);
              setUser(userResponse.data);
              try {
                const subResponse = await axios.get(`${API_URL}/api/billing/subscription`);
                setSubscription(subResponse.data);
              } catch (subErr) {
                console.error('Failed to load subscription:', subErr);
              }
              setSuccess('Payment received! Your subscription has been updated.');
              setCurrentView('billing');
            } catch {
              setError('Payment completed. Please log in to see your updated plan.');
            }
          }
        } else if (stripeCanceled) {
          // Payment was canceled
          try {
            const userResponse = await axios.get(`${API_URL}/api/auth/me`);
            setUser(userResponse.data);
          } catch {
            // Not logged in, that's ok
          }
          setError('Payment was canceled. You can try again anytime.');
        }
        
        // Clean URL
        window.history.replaceState({}, document.title, window.location.pathname);
        setAuthLoading(false);
        return;
      }
      
      // Check existing session
      try {
        const response = await axios.get(`${API_URL}/api/auth/me`);
        setUser(response.data);
      } catch (err) {
        // Not authenticated - that's fine
        console.log('Not authenticated');
      }
      setAuthLoading(false);
    };
    
    checkAuth();
  }, []);

  // Load data when user is authenticated
  useEffect(() => {
    if (user) {
      // Admin users only need admin data
      if (user.is_admin) {
        loadAdminStats();
        loadAdminUsers();
        return;
      }
      
      // Regular users load their data
      loadClientsCount();
      loadRecentMealPlans();
      loadSubscription();
      loadTeamStatus();
      // Load templates and categories if on Unlimited plan
      if (user.plan === 'unlimited') {
        loadTemplates();
        loadTemplateCategories();
        loadTeamMembers();
      }
    }
  }, [user]);

  const loadSubscription = async () => {
    try {
      // Use fetch for better mobile compatibility
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_URL}/api/billing/subscription`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        
      });
      
      if (res.ok) {
        const data = await res.json();
        setSubscription(data);
      }
    } catch (err) {
      console.error('Failed to load subscription:', err);
    }
  };

  const loadClientsCount = async () => {
    try {
      // Use fetch for better mobile compatibility
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_URL}/api/clients/count`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        
      });
      
      if (res.ok) {
        const data = await res.json();
        setClientsCount(data.count);
      }
    } catch (err) {
      console.error('Failed to load clients count:', err);
    }
  };

  const loadClients = async () => {
    console.log('=== LOAD CLIENTS CALLED ===');
    try {
      setLoading(true);
      // Use fetch for better mobile compatibility
      const token = localStorage.getItem('auth_token');
      console.log('Token from localStorage:', token ? 'YES (' + token.substring(0, 15) + '...)' : 'NO TOKEN!');
      
      if (!token) {
        console.error('Cannot load clients - no auth token in localStorage!');
        setError('Session expired. Please log in again.');
        setLoading(false);
        return;
      }
      
      const res = await fetch(`${API_URL}/api/clients`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        
      });
      
      console.log('Load clients response status:', res.status);
      
      if (!res.ok) {
        const errorText = await res.text();
        console.error('Load clients error:', res.status, errorText);
        if (res.status === 401) {
          setError('Session expired. Please log in again.');
          localStorage.removeItem('auth_token');
        } else {
          setError('Failed to load clients');
        }
        throw new Error('Failed to load clients');
      }
      
      const data = await res.json();
      console.log('Clients loaded:', data.length, 'clients');
      if (data.length > 0) {
        console.log('First client:', data[0].first_name, data[0].last_name);
      }
      setClients(data);
      setClientsCount(data.length);
    } catch (err) {
      console.error('Load clients exception:', err);
      setError('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const loadRecentMealPlans = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/meal-plans/recent?limit=5`);
      setRecentMealPlans(response.data);
    } catch (err) {
      console.error('Failed to load recent meal plans:', err);
    }
  };

  const loadMealPlans = async (clientId) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/meal-plans/client/${clientId}`);
      setMealPlans(response.data);
    } catch (err) {
      setError('Failed to load meal plans');
    } finally {
      setLoading(false);
    }
  };

  const loadWeightHistory = useCallback(async (clientId) => {
    try {
      const response = await axios.get(`${API_URL}/api/clients/${clientId}/weight-history`);
      const history = response.data.weight_history || [];
      const chartData = history.map(entry => ({
        date: new Date(entry.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }),
        weight: entry.weight,
        note: entry.note
      }));
      setWeightHistory(chartData);
    } catch (err) {
      console.error('Failed to load weight history:', err);
    }
  }, []);

  // Auth handlers - simple fetch for maximum compatibility
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    let res;
    try {
      res = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginForm.email, password: loginForm.password })
      });
    } catch (fetchErr) {
      setError('Unable to connect. Please check your connection.');
      setLoading(false);
      return;
    }
    
    let data = {};
    try {
      const text = await res.text();
      if (text) data = JSON.parse(text);
    } catch (parseErr) {
      // Ignore parse errors
    }
    
    if (!res.ok) {
      setError(data.detail || 'Invalid email or password');
      setLoading(false);
      return;
    }
    
    console.log('=== LOGIN SUCCESS ===');
    console.log('User ID:', data.user?.user_id);
    console.log('User Email:', data.user?.email);
    console.log('Auth token received:', data.auth_token ? 'YES (' + data.auth_token.substring(0, 15) + '...)' : 'NO');
    
    if (data.auth_token) {
      localStorage.setItem('auth_token', data.auth_token);
      // Immediately verify it was stored
      const storedToken = localStorage.getItem('auth_token');
      console.log('Token stored and retrieved:', storedToken ? 'YES (' + storedToken.substring(0, 15) + '...)' : 'FAILED TO STORE!');
      
      if (storedToken !== data.auth_token) {
        console.error('TOKEN MISMATCH! Storage may be blocked.');
      }
    } else {
      console.error('NO AUTH TOKEN IN LOGIN RESPONSE!');
    }
    
    if (data.user) {
      setUser(data.user);
      setLoginForm({ email: '', password: '' });
    }
    setLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    if (registerForm.password !== registerForm.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }
    
    let res;
    try {
      res = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: registerForm.name,
          email: registerForm.email,
          password: registerForm.password
        })
      });
    } catch (fetchErr) {
      setError('Unable to connect. Please check your connection.');
      setLoading(false);
      return;
    }
    
    let data = {};
    try {
      const text = await res.text();
      if (text) data = JSON.parse(text);
    } catch (parseErr) {
      // Ignore parse errors
    }
    
    if (!res.ok) {
      let errorMsg = 'Registration failed';
      if (data.detail) {
        if (typeof data.detail === 'string') {
          errorMsg = data.detail;
        } else if (Array.isArray(data.detail)) {
          errorMsg = data.detail.map(e => e.msg || e.message || JSON.stringify(e)).join('. ');
        }
      }
      setError(errorMsg);
      setLoading(false);
      return;
    }
    
    if (data.auth_token) {
      localStorage.setItem('auth_token', data.auth_token);
    }
    if (data.user) {
      setUser(data.user);
      setSuccess(data.message || 'Account created!');
      setRegisterForm({ name: '', email: '', password: '', confirmPassword: '' });
    } else {
      setSuccess('Account created! Please log in.');
      setAuthView('login');
      setLoginForm({ email: registerForm.email, password: '' });
      setRegisterForm({ name: '', email: '', password: '', confirmPassword: '' });
    }
    setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      const response = await axios.post(`${API_URL}/api/auth/forgot-password`, {
        email: forgotPasswordEmail
      });
      setSuccess(response.data.message);
      setForgotPasswordEmail('');
      // Switch back to login view after 3 seconds
      setTimeout(() => {
        setAuthView('login');
        setSuccess(null);
      }, 3000);
    } catch (err) {
      setError(parseErrorMessage(err, 'Failed to process request'));
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    if (resetPasswordForm.newPassword !== resetPasswordForm.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }
    
    if (resetPasswordForm.newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.post(`${API_URL}/api/auth/reset-password`, {
        token: resetPasswordForm.token,
        new_password: resetPasswordForm.newPassword
      });
      setSuccess(response.data.message);
      setResetPasswordForm({ token: '', newPassword: '', confirmPassword: '' });
      // Clear URL params and switch to login
      window.history.replaceState({}, document.title, window.location.pathname);
      setTimeout(() => {
        setAuthView('login');
        setSuccess(null);
      }, 3000);
    } catch (err) {
      setError(parseErrorMessage(err, 'Failed to reset password'));
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!user?.email) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.post(`${API_URL}/api/auth/resend-verification`, {
        email: user.email
      });
      setSuccess(response.data.message || 'Verification email sent!');
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      setError(parseErrorMessage(err, 'Failed to send verification email'));
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`);
    } catch (err) {
      console.error('Logout error:', err);
    }
    // Clear localStorage token (for mobile fallback auth)
    localStorage.removeItem('auth_token');
    setUser(null);
    setCurrentView('home');
    setClients([]);
    setSelectedClient(null);
  };

  // Client handlers
  const handleCreateClient = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const clientData = {
        ...formData,
        // Convert arrays properly
        dietary_restrictions: Array.isArray(formData.dietary_restrictions) 
          ? formData.dietary_restrictions 
          : (formData.dietary_restrictions ? formData.dietary_restrictions.split(',').map(s => s.trim()).filter(Boolean) : []),
        allergies: Array.isArray(formData.allergies) 
          ? formData.allergies 
          : (formData.allergies ? formData.allergies.split(',').map(s => s.trim()).filter(Boolean) : []),
        age: parseInt(formData.age),
        height: parseFloat(formData.height),
        weight: parseFloat(formData.weight),
        goal_weight: formData.goal_weight ? parseFloat(formData.goal_weight) : null,
        num_meals: parseInt(formData.num_meals),
        num_snacks: parseInt(formData.num_snacks),
        manual_calories: formData.manual_calories ? parseInt(formData.manual_calories) : null,
        manual_protein_pct: formData.manual_protein_pct ? parseFloat(formData.manual_protein_pct) : null,
        manual_carbs_pct: formData.manual_carbs_pct ? parseFloat(formData.manual_carbs_pct) : null,
        manual_fat_pct: formData.manual_fat_pct ? parseFloat(formData.manual_fat_pct) : null,
        manual_protein_grams: formData.manual_protein_grams ? parseInt(formData.manual_protein_grams) : null,
        manual_carbs_grams: formData.manual_carbs_grams ? parseInt(formData.manual_carbs_grams) : null,
        manual_fat_grams: formData.manual_fat_grams ? parseInt(formData.manual_fat_grams) : null,
        bmr_equation: formData.bmr_equation || 'harris_benedict',
        body_fat_pct: formData.body_fat_pct ? parseFloat(formData.body_fat_pct) : null,
        protein_per_kg: formData.protein_per_kg ? parseFloat(formData.protein_per_kg) : null
      };

      // Use fetch for iOS Safari compatibility
      const authToken = localStorage.getItem('auth_token');
      console.log('Creating client - token exists:', !!authToken);
      
      let res;
      try {
        res = await fetch(`${API_URL}/api/clients`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(authToken && { 'Authorization': `Bearer ${authToken}` })
          },
          body: JSON.stringify(clientData)
        });
        console.log('Create client response status:', res.status);
      } catch (fetchErr) {
        console.error('Create client fetch error:', fetchErr);
        setError('Unable to connect. Please check your connection.');
        setLoading(false);
        return;
      }
      
      let data = {};
      try {
        const text = await res.text();
        console.log('Create client response text:', text.substring(0, 200));
        if (text) data = JSON.parse(text);
      } catch (parseErr) {
        console.error('Parse error:', parseErr);
      }
      
      if (!res.ok) {
        // Handle validation errors (422) with detailed messages
        let errorMsg = `Failed to create client (${res.status})`;
        if (data.detail) {
          if (typeof data.detail === 'string') {
            errorMsg = data.detail;
          } else if (Array.isArray(data.detail)) {
            // Pydantic validation errors
            errorMsg = data.detail.map(e => {
              const field = e.loc?.slice(-1)[0] || 'field';
              return `${field}: ${e.msg}`;
            }).join(', ');
          }
        } else if (res.status === 401) {
          errorMsg = 'Session expired. Please log in again.';
        } else if (res.status === 403) {
          errorMsg = 'You do not have permission to add clients.';
        }
        console.error('Client creation failed:', res.status, 'detail:', JSON.stringify(data));
        setError(errorMsg);
        setLoading(false);
        return;
      }
      
      setSuccess('Client created successfully!');
      console.log('CLIENT CREATED SUCCESSFULLY:', JSON.stringify({
        id: data.id,
        user_id: data.user_id,
        first_name: data.first_name,
        last_name: data.last_name,
        message: data.message
      }));
      setFormData({
        first_name: '', last_name: '', email: '', phone: '', age: '',
        gender: 'male', height: '', weight: '', goal_weight: '',
        activity_level: 'moderately_active', dietary_restrictions: [],
        num_meals: 3, num_snacks: 2, allergies: [], food_preferences: '',
        manual_calories: '', manual_protein_pct: '', manual_carbs_pct: '', manual_fat_pct: '',
        manual_protein_grams: '', manual_carbs_grams: '', manual_fat_grams: '',
        bmr_equation: 'harris_benedict', body_fat_pct: '', protein_per_kg: ''
      });

      setTimeout(() => {
        setSuccess(null);
        setSelectedClient(data);
        setCurrentView('client-detail');
      }, 1500);
    } catch (err) {
      setError('Failed to create client. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateClient = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const clientData = {
        ...editFormData,
        // Convert arrays properly
        dietary_restrictions: Array.isArray(editFormData.dietary_restrictions) 
          ? editFormData.dietary_restrictions 
          : (editFormData.dietary_restrictions ? editFormData.dietary_restrictions.split(',').map(s => s.trim()).filter(Boolean) : []),
        allergies: Array.isArray(editFormData.allergies) 
          ? editFormData.allergies 
          : (editFormData.allergies ? editFormData.allergies.split(',').map(s => s.trim()).filter(Boolean) : []),
        age: parseInt(editFormData.age),
        height: parseFloat(editFormData.height),
        weight: parseFloat(editFormData.weight),
        goal_weight: editFormData.goal_weight ? parseFloat(editFormData.goal_weight) : null,
        num_meals: parseInt(editFormData.num_meals),
        num_snacks: parseInt(editFormData.num_snacks),
        manual_calories: editFormData.manual_calories ? parseInt(editFormData.manual_calories) : null,
        manual_protein_pct: editFormData.manual_protein_pct ? parseFloat(editFormData.manual_protein_pct) : null,
        manual_carbs_pct: editFormData.manual_carbs_pct ? parseFloat(editFormData.manual_carbs_pct) : null,
        manual_fat_pct: editFormData.manual_fat_pct ? parseFloat(editFormData.manual_fat_pct) : null,
        manual_protein_grams: editFormData.manual_protein_grams ? parseInt(editFormData.manual_protein_grams) : null,
        manual_carbs_grams: editFormData.manual_carbs_grams ? parseInt(editFormData.manual_carbs_grams) : null,
        manual_fat_grams: editFormData.manual_fat_grams ? parseInt(editFormData.manual_fat_grams) : null,
        bmr_equation: editFormData.bmr_equation || 'harris_benedict',
        body_fat_pct: editFormData.body_fat_pct ? parseFloat(editFormData.body_fat_pct) : null,
        protein_per_kg: editFormData.protein_per_kg ? parseFloat(editFormData.protein_per_kg) : null
      };

      // Use fetch for iOS Safari compatibility
      const authToken = localStorage.getItem('auth_token');
      let res;
      try {
        res = await fetch(`${API_URL}/api/clients/${selectedClient.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...(authToken && { 'Authorization': `Bearer ${authToken}` })
          },
          body: JSON.stringify(clientData)
        });
      } catch (fetchErr) {
        setError('Unable to connect. Please check your connection.');
        setLoading(false);
        return;
      }
      
      let data = {};
      try {
        const text = await res.text();
        if (text) data = JSON.parse(text);
      } catch (parseErr) {
        // Ignore parse errors
      }
      
      if (!res.ok) {
        setError(data.detail || 'Failed to update client');
        setLoading(false);
        return;
      }
      
      setSelectedClient(data);
      setSuccess('Client updated successfully!');
      setEditingClient(false);
      setEditFormData(null);
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(parseErrorMessage(err, 'Failed to update client'));
    } finally {
      setLoading(false);
    }
  };

  const showConfirm = (title, message, onConfirm) => {
    setConfirmModal({ show: true, title, message, onConfirm });
  };

  const hideConfirm = () => {
    setConfirmModal({ show: false, title: '', message: '', onConfirm: null });
  };

  const handleDeleteClient = (clientId) => {
    showConfirm(
      'Delete Client',
      'Are you sure you want to delete this client? This will also delete all their meal plans.',
      async () => {
        hideConfirm();
        try {
          setLoading(true);
          await axios.delete(`${API_URL}/api/clients/${clientId}`);
          setSuccess('Client deleted successfully');
          setSelectedClient(null);
          setCurrentView('clients');
          loadClients();
          loadClientsCount();
          setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
          setError(parseErrorMessage(err, 'Failed to delete client'));
        } finally {
          setLoading(false);
        }
      }
    );
  };

  const handleDeleteMealPlan = (mealPlanId) => {
    showConfirm(
      'Delete Meal Plan',
      'Delete this meal plan? This cannot be undone.',
      async () => {
        hideConfirm();
        try {
          setLoading(true);
          await axios.delete(`${API_URL}/api/meal-plans/${mealPlanId}`);
          setSuccess('Meal plan deleted');
          if (selectedClient) {
            await loadMealPlans(selectedClient.id);
          }
          loadRecentMealPlans();
          setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
          setError(parseErrorMessage(err, 'Failed to delete meal plan'));
        } finally {
          setLoading(false);
        }
      }
    );
  };

  // Meal plan handlers
  const handleGenerateMealPlan = async (clientId) => {
    setLoading(true);
    setGeneratingMealPlan(true);
    setGenerationProgress(0);
    setGenerationStep('Analyzing client profile...');
    setError(null);

    // Simulate progress steps while waiting for AI (slower progression to avoid "stuck" feeling)
    const progressSteps = [
      { progress: 5, step: 'Analyzing client profile...', delay: 800 },
      { progress: 12, step: 'Calculating macro requirements...', delay: 2500 },
      { progress: 20, step: 'Selecting optimal ingredients...', delay: 5000 },
      { progress: 28, step: 'Generating breakfast options...', delay: 8000 },
      { progress: 38, step: 'Creating lunch recipes...', delay: 12000 },
      { progress: 48, step: 'Designing dinner meals...', delay: 18000 },
      { progress: 58, step: 'Adding healthy snacks...', delay: 25000 },
      { progress: 68, step: 'Building shopping list...', delay: 35000 },
      { progress: 78, step: 'Optimizing nutritional balance...', delay: 50000 },
      { progress: 85, step: 'Finalizing meal plan...', delay: 70000 },
    ];

    const progressTimers = progressSteps.map(({ progress, step, delay }) => 
      setTimeout(() => {
        setGenerationProgress(progress);
        setGenerationStep(step);
      }, delay)
    );

    try {
      // Use longer timeout for AI generation (120 seconds)
      const response = await axios.post(`${API_URL}/api/meal-plans`, {
        client_id: clientId,
        week_starting: new Date().toISOString(),
        generate_shopping_list: true,
        template_option: mealPlanOptions.template_option,
        calorie_cycling: mealPlanOptions.calorie_cycling,
        high_calorie_days: mealPlanOptions.high_calorie_days
      }, { timeout: 120000 });

      // Clear progress timers
      progressTimers.forEach(timer => clearTimeout(timer));
      
      setGenerationProgress(100);
      setGenerationStep('Complete!');
      
      setTimeout(() => {
        setSelectedMealPlan(response.data);
        setCurrentView('meal-plan-detail');
        setSuccess('Meal plan generated!');
        loadRecentMealPlans();
        setTimeout(() => setSuccess(null), 3000);
      }, 500);
    } catch (err) {
      progressTimers.forEach(timer => clearTimeout(timer));
      // Check if it's a timeout or network error (proxy timeout appears as network error)
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout') || err.message === 'Network Error' || !err?.response) {
        setGenerationStep('Checking if plan was created...');
        setGenerationProgress(95);
        
        // Wait 3 seconds then check if meal plan was actually created
        setTimeout(async () => {
          try {
            const checkResponse = await axios.get(`${API_URL}/api/meal-plans/client/${clientId}`, { timeout: 10000 });
            const recentPlans = checkResponse.data || [];
            // Check if a plan was created in the last 5 minutes
            const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
            const newPlan = recentPlans.find(p => new Date(p.created_at) > fiveMinutesAgo);
            
            if (newPlan) {
              setGenerationProgress(100);
              setGenerationStep('Complete!');
              setSelectedMealPlan(newPlan);
              setCurrentView('meal-plan-detail');
              setSuccess('Meal plan generated successfully!');
              loadRecentMealPlans();
              setTimeout(() => setSuccess(null), 3000);
            } else {
              setError('Meal plan generation is taking longer than expected. Please check your Client\'s Meal Plans in a moment.');
            }
          } catch (checkErr) {
            setError('Meal plan generation is taking longer than expected. Please check your Client\'s Meal Plans in a moment.');
          } finally {
            setGeneratingMealPlan(false);
            setGenerationProgress(0);
            setGenerationStep('');
          }
        }, 3000);
        return; // Don't run the finally block yet
      } else {
        setError(parseErrorMessage(err, 'Failed to generate meal plan'));
      }
    } finally {
      setLoading(false);
      setGeneratingMealPlan(false);
      setGenerationProgress(0);
      setGenerationStep('');
    }
  };

  const handleSwapMeal = async (mealPlanId, sampleKey, mealType) => {
    setSwappingMeal(`${sampleKey}-${mealType}`);
    setError(null);

    try {
      // Use longer timeout for AI swap (60 seconds)
      const response = await axios.post(
        `${API_URL}/api/meal-plans/${mealPlanId}/swap-meal?sample_key=${sampleKey}&meal_type=${mealType}`,
        {},
        { timeout: 60000 }
      );

      const updatedPlan = await axios.get(`${API_URL}/api/meal-plans/${mealPlanId}`);
      setSelectedMealPlan(updatedPlan.data);
      setSuccess('Meal swapped!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      // Check if it's a timeout or network error
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout') || err.message === 'Network Error' || !err?.response) {
        // Try to fetch the updated plan - swap may have succeeded
        try {
          const updatedPlan = await axios.get(`${API_URL}/api/meal-plans/${mealPlanId}`, { timeout: 10000 });
          setSelectedMealPlan(updatedPlan.data);
          setSuccess('Meal swap completed!');
          setTimeout(() => setSuccess(null), 3000);
        } catch (fetchErr) {
          setError('Meal swap is taking longer than expected. Please refresh the page to check if it completed.');
        }
      } else {
        setError(parseErrorMessage(err, 'Failed to swap meal'));
      }
    } finally {
      setSwappingMeal(null);
    }
  };

  // Handle custom meal request
  const handleCustomMealRequest = async () => {
    if (!customMealModal || !customMealPrompt.trim() || !selectedMealPlan) return;
    
    setSubmittingCustomMeal(true);
    setError(null);
    
    try {
      const response = await axios.post(
        `${API_URL}/api/meal-plans/${selectedMealPlan.id}/custom-meal`,
        {
          sample_key: customMealModal.sampleKey,
          meal_type: customMealModal.mealType,
          custom_prompt: customMealPrompt.trim()
        },
        { timeout: 60000 }
      );
      
      if (response.data.success) {
        // Refresh the meal plan
        const updatedPlan = await axios.get(`${API_URL}/api/meal-plans/${selectedMealPlan.id}`);
        setSelectedMealPlan(updatedPlan.data);
        setSuccess(`Custom meal created: ${response.data.new_meal.name}`);
        setTimeout(() => setSuccess(null), 4000);
        
        // Close modal and reset
        setCustomMealModal(null);
        setCustomMealPrompt('');
      }
    } catch (err) {
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout') || err.message === 'Network Error') {
        // Try to fetch the updated plan - request may have succeeded
        try {
          const updatedPlan = await axios.get(`${API_URL}/api/meal-plans/${selectedMealPlan.id}`, { timeout: 10000 });
          setSelectedMealPlan(updatedPlan.data);
          setSuccess('Custom meal created!');
          setTimeout(() => setSuccess(null), 3000);
          setCustomMealModal(null);
          setCustomMealPrompt('');
        } catch (fetchErr) {
          setError('Request is taking longer than expected. Please refresh the page.');
        }
      } else {
        setError(parseErrorMessage(err, 'Failed to create custom meal'));
      }
    } finally {
      setSubmittingCustomMeal(false);
    }
  };

  // Custom Meal Builder functions
  const openMealEditor = (sampleKey, mealType, meal) => {
    setEditingMealData({
      sample_key: sampleKey,
      meal_type: mealType,
      meal: {
        name: meal.name || '',
        calories: meal.calories || 0,
        protein: meal.protein || 0,
        carbs: meal.carbs || 0,
        fat: meal.fat || 0,
        ingredients: meal.ingredients || [],
        instructions: meal.instructions || ''
      }
    });
    setShowMealEditor(true);
  };

  const handleSaveMeal = async () => {
    if (!selectedMealPlan) return;
    
    setSavingMeal(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/meal-plans/${selectedMealPlan.id}/meal`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({
          sample_key: editingMealData.sample_key,
          meal_type: editingMealData.meal_type,
          meal_data: editingMealData.meal
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to save meal');
      }
      
      const data = await response.json();
      
      // Update local state with new meal data (including recalculated macros)
      setSelectedMealPlan(prev => {
        const updatedDailyMeals = {
          ...prev.daily_meals,
          [editingMealData.sample_key]: {
            ...prev.daily_meals[editingMealData.sample_key],
            [editingMealData.meal_type]: data.meal
          }
        };
        
        // Recalculate macro totals for the plan
        let totalCalories = 0, totalProtein = 0, totalCarbs = 0, totalFat = 0;
        Object.values(updatedDailyMeals).forEach(sample => {
          Object.values(sample).forEach(meal => {
            if (meal && typeof meal === 'object') {
              totalCalories += meal.calories || 0;
              totalProtein += meal.protein || 0;
              totalCarbs += meal.carbs || 0;
              totalFat += meal.fat || 0;
            }
          });
        });
        
        // Average across samples (typically 3)
        const sampleCount = Object.keys(updatedDailyMeals).length || 1;
        
        return {
          ...prev,
          daily_meals: updatedDailyMeals,
          macro_totals: {
            ...prev.macro_totals,
            calories: Math.round(totalCalories / sampleCount),
            protein: Math.round(totalProtein / sampleCount),
            carbohydrates: Math.round(totalCarbs / sampleCount),
            fat: Math.round(totalFat / sampleCount)
          }
        };
      });
      
      setShowMealEditor(false);
      const message = data.macros_recalculated 
        ? 'Meal updated & macros recalculated!' 
        : 'Meal updated successfully!';
      setSuccess(message);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Save meal error:', err);
      setError(err.message || 'Failed to save meal');
    } finally {
      setSavingMeal(false);
    }
  };

  const handleAddIngredient = () => {
    setEditingMealData(prev => ({
      ...prev,
      meal: {
        ...prev.meal,
        ingredients: [...prev.meal.ingredients, { name: '', quantity: '' }]
      }
    }));
  };

  const handleRemoveIngredient = (index) => {
    setEditingMealData(prev => ({
      ...prev,
      meal: {
        ...prev.meal,
        ingredients: prev.meal.ingredients.filter((_, i) => i !== index)
      }
    }));
  };

  const handleIngredientChange = (index, field, value) => {
    setEditingMealData(prev => ({
      ...prev,
      meal: {
        ...prev.meal,
        ingredients: prev.meal.ingredients.map((ing, i) => 
          i === index ? { ...ing, [field]: value } : ing
        )
      }
    }));
  };

  const handleDeleteMeal = async (sampleKey, mealType) => {
    if (!selectedMealPlan) return;
    
    setConfirmModal({
      show: true,
      title: 'Delete Meal',
      message: `Are you sure you want to delete this ${mealType.replace('_', ' ')}?`,
      onConfirm: async () => {
        setConfirmModal({ show: false });
        setLoading(true);
        
        try {
          const response = await fetch(
            `${API_URL}/api/meal-plans/${selectedMealPlan.id}/meal?sample_key=${sampleKey}&meal_type=${mealType}`,
            {
              method: 'DELETE',
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
              }
            }
          );
          
          if (!response.ok) {
            throw new Error('Failed to delete meal');
          }
          
          // Update local state
          setSelectedMealPlan(prev => {
            const newDailyMeals = { ...prev.daily_meals };
            if (newDailyMeals[sampleKey]) {
              delete newDailyMeals[sampleKey][mealType];
            }
            return { ...prev, daily_meals: newDailyMeals };
          });
          
          setSuccess('Meal deleted successfully!');
          setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
          console.error('Delete meal error:', err);
          setError('Failed to delete meal');
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const openPdfPreview = (plan) => {
    setPdfPreviewPlan(plan);
    setShowPdfPreview(true);
  };

  const handleExportPDF = async (mealPlanId) => {
    setLoading(true);
    setShowPdfPreview(false);
    try {
      const response = await axios.get(`${API_URL}/api/meal-plans/${mealPlanId}/export-pdf`, {
        responseType: 'blob'
      });
      
      // Create blob from response
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      // Create temporary link and trigger download
      const link = document.createElement('a');
      link.style.display = 'none';
      link.href = url;
      link.download = `meal_plan_${new Date().toISOString().split('T')[0]}.pdf`;
      
      // Append to body, click, and cleanup
      document.body.appendChild(link);
      link.click();
      
      // Cleanup after a short delay to ensure download starts
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      setSuccess('PDF downloaded successfully! Check your Downloads folder.');
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      console.error('PDF export error:', err);
      setError(parseErrorMessage(err, 'Failed to export PDF. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicateMealPlan = async () => {
    if (!selectedMealPlan) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.post(`${API_URL}/api/meal-plans/${selectedMealPlan.id}/duplicate`, {
        target_client_id: duplicateTargetClient || null
      });
      
      setSuccess(`Meal plan duplicated for ${response.data.client_name}!`);
      setShowDuplicateModal(false);
      setDuplicateTargetClient('');
      loadRecentMealPlans();
      
      const newPlanResponse = await axios.get(`${API_URL}/api/meal-plans/${response.data.id}`);
      setSelectedMealPlan(newPlanResponse.data);
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(parseErrorMessage(err, 'Failed to duplicate meal plan'));
    } finally {
      setLoading(false);
    }
  };

  const openDuplicateModal = async () => {
    if (clients.length === 0) {
      await loadClients();
    }
    setDuplicateTargetClient(selectedMealPlan?.client_id || '');
    setShowDuplicateModal(true);
  };

  // Quick meal plan
  const handleGenerateQuickMealPlan = async () => {
    if (!quickMealData.calories) {
      setError('Please enter target calories');
      return;
    }

    setLoading(true);
    setGeneratingMealPlan(true);
    setGenerationProgress(0);
    setGenerationStep('Calculating macros...');
    setError(null);
    setQuickMealResult(null);

    // Simulate progress (slower progression to avoid "stuck" feeling)
    const progressSteps = [
      { progress: 8, step: 'Calculating macros...', delay: 800 },
      { progress: 20, step: 'Selecting balanced ingredients...', delay: 3000 },
      { progress: 35, step: 'Creating meal combinations...', delay: 7000 },
      { progress: 50, step: 'Optimizing nutrition...', delay: 12000 },
      { progress: 65, step: 'Balancing portions...', delay: 20000 },
      { progress: 78, step: 'Finalizing your plan...', delay: 35000 },
    ];

    const progressTimers = progressSteps.map(({ progress, step, delay }) => 
      setTimeout(() => {
        setGenerationProgress(progress);
        setGenerationStep(step);
      }, delay)
    );

    try {
      // Use longer timeout for AI generation (120 seconds)
      const response = await axios.post(`${API_URL}/api/quick-meal-plan`, {
        calories: parseInt(quickMealData.calories),
        num_meals: quickMealData.num_meals,
        protein_pct: parseFloat(quickMealData.protein_pct || 30),
        carbs_pct: parseFloat(quickMealData.carbs_pct || 40),
        fat_pct: parseFloat(quickMealData.fat_pct || 30),
        dietary_restrictions: quickMealData.dietary_restrictions
      }, { timeout: 120000 });

      progressTimers.forEach(timer => clearTimeout(timer));
      setGenerationProgress(100);
      setGenerationStep('Complete!');

      setTimeout(() => {
        setQuickMealResult(response.data);
        setSuccess('Meal plan generated!');
        setTimeout(() => setSuccess(null), 3000);
      }, 300);
    } catch (err) {
      progressTimers.forEach(timer => clearTimeout(timer));
      // Check if it's a timeout error
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        setError('Meal plan generation is taking longer than expected. Please try again.');
      } else {
        setError(parseErrorMessage(err, 'Failed to generate meal plan'));
      }
    } finally {
      setLoading(false);
      setGeneratingMealPlan(false);
      setGenerationProgress(0);
      setGenerationStep('');
    }
  };

  // Export quick meal plan as PDF
  const handleExportQuickMealPDF = async () => {
    if (!quickMealResult) return;
    
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/quick-meal-plan/export-pdf`, {
        daily_meals: quickMealResult.daily_meals,
        target_calories: quickMealResult.target_calories,
        macro_totals: quickMealResult.macro_totals
      }, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.style.display = 'none';
      link.href = url;
      link.download = `quick_meal_plan_${new Date().toISOString().split('T')[0]}.pdf`;
      
      document.body.appendChild(link);
      link.click();
      
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      setSuccess('PDF downloaded successfully!');
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      console.error('Quick meal PDF export error:', err);
      setError(parseErrorMessage(err, 'Failed to export PDF'));
    } finally {
      setLoading(false);
    }
  };

  // Weight tracking
  const handleAddWeight = async () => {
    if (!newWeight.weight || !selectedClient) return;
    
    try {
      setLoading(true);
      await axios.post(`${API_URL}/api/clients/${selectedClient.id}/weight`, {
        weight: parseFloat(newWeight.weight),
        note: newWeight.note || null
      });
      
      setSuccess('Weight entry added!');
      setNewWeight({ weight: '', note: '' });
      setShowAddWeight(false);
      loadWeightHistory(selectedClient.id);
      
      const updatedClient = await axios.get(`${API_URL}/api/clients/${selectedClient.id}`);
      setSelectedClient(updatedClient.data);
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(parseErrorMessage(err, 'Failed to add weight entry'));
    } finally {
      setLoading(false);
    }
  };

  // Templates handlers (Unlimited plan)
  const loadTemplates = async (category = '') => {
    try {
      const url = category 
        ? `${API_URL}/api/templates?category=${encodeURIComponent(category)}`
        : `${API_URL}/api/templates`;
      const response = await axios.get(url);
      setTemplates(response.data.templates || []);
    } catch (err) {
      console.error('Failed to load templates:', err);
    }
  };

  const loadTemplateCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/templates/categories`);
      setTemplateCategories(response.data.categories || []);
    } catch (err) {
      console.error('Failed to load template categories:', err);
    }
  };

  const handleSaveTemplate = async () => {
    if (!selectedMealPlan || !newTemplateForm.name) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.post(`${API_URL}/api/templates`, {
        name: newTemplateForm.name,
        category: newTemplateForm.category,
        description: newTemplateForm.description,
        meal_plan_id: selectedMealPlan.id
      });
      
      setSuccess(response.data.message || 'Template saved!');
      setShowSaveTemplateModal(false);
      setNewTemplateForm({ name: '', category: 'General', description: '' });
      loadTemplates();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(parseErrorMessage(err, 'Failed to save template'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    showConfirm(
      'Delete Template',
      'Are you sure you want to delete this template?',
      async () => {
        hideConfirm();
        try {
          setLoading(true);
          await axios.delete(`${API_URL}/api/templates/${templateId}`);
          setSuccess('Template deleted');
          loadTemplates(selectedTemplateCategory);
          setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
          setError(parseErrorMessage(err, 'Failed to delete template'));
        } finally {
          setLoading(false);
        }
      }
    );
  };

  const handleApplyTemplate = async (templateId, clientId) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.post(`${API_URL}/api/templates/${templateId}/apply`, {
        client_id: clientId
      });
      
      setSuccess(response.data.message || 'Template applied!');
      setShowApplyTemplateModal(false);
      setSelectedTemplateToApply(null);
      
      // Navigate to the new meal plan
      if (response.data.meal_plan_id) {
        const planResponse = await axios.get(`${API_URL}/api/meal-plans/${response.data.meal_plan_id}`);
        setSelectedMealPlan(planResponse.data);
        setCurrentView('meal-plan-detail');
      }
      
      loadRecentMealPlans();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(parseErrorMessage(err, 'Failed to apply template'));
    } finally {
      setLoading(false);
    }
  };

  // Team handlers (Unlimited plan)
  const loadTeamStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/team/status`);
      setTeamStatus(response.data);
    } catch (err) {
      console.error('Failed to load team status:', err);
    }
  };

  const loadTeamMembers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/team/members`);
      setTeamMembers(response.data.members || []);
    } catch (err) {
      console.error('Failed to load team members:', err);
    }
  };

  const handleInviteTeamMember = async () => {
    if (!inviteForm.email || !inviteForm.name || !inviteForm.password) {
      setError('Please fill in all fields');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.post(`${API_URL}/api/team/invite`, inviteForm);
      setSuccess(response.data.message || 'Team member invited!');
      setShowInviteModal(false);
      setInviteForm({ email: '', name: '', password: '' });
      loadTeamMembers();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(parseErrorMessage(err, 'Failed to invite team member'));
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveTeamMember = async (memberId, memberName) => {
    showConfirm(
      'Remove Team Member',
      `Are you sure you want to remove ${memberName} from your team?`,
      async () => {
        hideConfirm();
        try {
          setLoading(true);
          await axios.delete(`${API_URL}/api/team/${memberId}`);
          setSuccess('Team member removed');
          loadTeamMembers();
          setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
          setError(parseErrorMessage(err, 'Failed to remove team member'));
        } finally {
          setLoading(false);
        }
      }
    );
  };

  // Admin functions
  const loadAdminStats = async () => {
    if (!isAdmin()) return;
    try {
      const response = await axios.get(`${API_URL}/api/admin/stats`);
      setAdminStats(response.data);
    } catch (err) {
      console.error('Failed to load admin stats:', err);
    }
  };

  const loadAdminUsers = async (page = 1, filterOverrides = null) => {
    if (!isAdmin()) return;
    setAdminLoading(true);
    try {
      // Use filter overrides if provided, otherwise use current state
      const filters = filterOverrides || adminFilters;
      
      const params = new URLSearchParams({
        page: page.toString(),
        limit: adminPagination.limit.toString()
      });
      if (filters.plan) params.append('plan', filters.plan);
      if (filters.status) params.append('status', filters.status);
      if (filters.search) params.append('search', filters.search);
      
      const response = await axios.get(`${API_URL}/api/admin/users?${params}`);
      setAdminUsers(response.data.users);
      setAdminPagination(response.data.pagination);
    } catch (err) {
      setError(parseErrorMessage(err, 'Failed to load users'));
    } finally {
      setAdminLoading(false);
    }
  };

  const loadAdminUserDetail = async (userId) => {
    if (!isAdmin()) return;
    setAdminLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/users/${userId}`);
      setSelectedAdminUser(response.data);
    } catch (err) {
      setError(parseErrorMessage(err, 'Failed to load user details'));
    } finally {
      setAdminLoading(false);
    }
  };

  const handleAdminUpdatePlan = async (userId, plan, status) => {
    if (!isAdmin()) return;
    try {
      await axios.post(`${API_URL}/api/admin/users/${userId}/update-plan?plan=${plan}&plan_status=${status}`);
      setSuccess('User plan updated successfully');
      setTimeout(() => setSuccess(null), 3000);
      loadAdminUsers(adminPagination.page);
      if (selectedAdminUser?.user_id === userId) {
        loadAdminUserDetail(userId);
      }
    } catch (err) {
      setError(parseErrorMessage(err, 'Failed to update user plan'));
    }
  };

  const handleAdminDeleteUser = async (userId) => {
    if (!isAdmin()) return;
    if (!window.confirm('Are you sure you want to delete this user and ALL their data? This cannot be undone.')) {
      return;
    }
    try {
      await axios.delete(`${API_URL}/api/admin/users/${userId}`);
      setSuccess('User deleted successfully');
      setTimeout(() => setSuccess(null), 3000);
      setSelectedAdminUser(null);
      loadAdminUsers(adminPagination.page);
      loadAdminStats();
    } catch (err) {
      setError(parseErrorMessage(err, 'Failed to delete user'));
    }
  };

  const handleAdminDeactivateUser = async (userId) => {
    if (!isAdmin()) return;
    try {
      const response = await axios.post(`${API_URL}/api/admin/users/${userId}/deactivate`);
      setSuccess(response.data.message);
      setTimeout(() => setSuccess(null), 3000);
      loadAdminUsers(adminPagination.page);
      if (selectedAdminUser?.user_id === userId) {
        loadAdminUserDetail(userId);
      }
    } catch (err) {
      setError(parseErrorMessage(err, 'Failed to deactivate user'));
    }
  };


  const handleAdminSetAdmin = async (userId, isAdminStatus) => {
    if (!isAdmin()) return;
    try {
      await axios.post(`${API_URL}/api/admin/users/${userId}/set-admin?is_admin=${isAdminStatus}`);
      setSuccess(`Admin privileges ${isAdminStatus ? 'granted' : 'revoked'}`);
      setTimeout(() => setSuccess(null), 3000);
      loadAdminUsers(adminPagination.page);
      if (selectedAdminUser?.user_id === userId) {
        loadAdminUserDetail(userId);
      }
    } catch (err) {
      setError(parseErrorMessage(err, 'Failed to update admin status'));
    }
  };

  // Billing handlers
  const handleSubscribe = async (plan) => {
    setLoading(true);
    setError(null);
    
    try {
      // Determine if this is an upgrade/downgrade or new subscription
      const isChangingPlan = subscription?.plan_status === 'active' && subscription?.plan !== plan;
      const endpoint = isChangingPlan ? 'change-plan' : 'checkout';
      
      // Get auth token for mobile compatibility
      const authToken = localStorage.getItem('auth_token');
      
      // Use fetch for iOS Safari compatibility
      const response = await fetch(`${API_URL}/api/billing/${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(authToken && { 'Authorization': `Bearer ${authToken}` })
        },
        body: JSON.stringify({
          plan: plan,
          origin_url: window.location.origin
        })
      });
      
      const text = await response.text();
      let data = {};
      if (text) {
        try {
          data = JSON.parse(text);
        } catch {
          setError('Invalid server response');
          setLoading(false);
          return;
        }
      }
      
      if (!response.ok) {
        setError(data.detail || 'Failed to process subscription');
        setLoading(false);
        return;
      }
      
      // If we get a checkout URL, redirect to Stripe
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } 
      // If plan was changed directly (pro-rata), update local state
      else if (data.success && data.new_plan) {
        setSuccess(data.message || 'Plan changed successfully!');
        // Reload both user and subscription to reflect the change
        try {
          const userResponse = await axios.get(`${API_URL}/api/auth/me`);
          setUser(userResponse.data);
        } catch {}
        loadSubscription();
        setTimeout(() => setSuccess(null), 5000);
        setLoading(false);
      }
    } catch {
      setError('Connection failed. Please try again.');
      setLoading(false);
    }
  };

  // Settings handlers
  const handleSaveBranding = async () => {
    setLoading(true);
    setError(null);
    
    try {
      await axios.post(`${API_URL}/api/settings`, brandingForm);
      setSuccess('Branding settings saved!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(parseErrorMessage(err, 'Failed to save branding'));
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingLogo(true);
    setError(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API_URL}/api/settings/logo`, formData);
      setUser(prev => ({ ...prev, logo_url: response.data.logo_url }));
      setSuccess('Logo uploaded successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(parseErrorMessage(err, 'Failed to upload logo'));
    } finally {
      setUploadingLogo(false);
    }
  };

  // Cancel Subscription handler
  const handleCancelSubscription = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/billing/cancel-subscription`);
      if (response.data.success) {
        setSuccess(response.data.message);
        loadSubscription();
        // Reload user data too
        try {
          const userResponse = await axios.get(`${API_URL}/api/auth/me`);
          setUser(userResponse.data);
        } catch {}
        setTimeout(() => setSuccess(null), 8000);
      }
    } catch (err) {
      setError(parseErrorMessage(err, 'Failed to cancel subscription'));
    }
  };

  // Delete Account handler
  const handleDeleteAccount = async (password) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/delete-account`, { password });
      if (response.data.success) {
        // Clear everything and return to login
        localStorage.removeItem('auth_token');
        setUser(null);
        setSubscription(null);
        setCurrentView('home');
        setSuccess('Your account has been permanently deleted.');
        setTimeout(() => setSuccess(null), 5000);
        return { success: true };
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to delete account';
      return { success: false, error: msg };
    }
  };

  const handleEditClient = () => {
    setEditFormData({
      first_name: selectedClient.first_name,
      last_name: selectedClient.last_name,
      email: selectedClient.email,
      phone: selectedClient.phone || '',
      age: selectedClient.age,
      gender: selectedClient.gender,
      height: selectedClient.height,
      weight: selectedClient.weight,
      goal_weight: selectedClient.goal_weight || '',
      activity_level: selectedClient.activity_level,
      dietary_restrictions: selectedClient.dietary_restrictions || [],
      num_meals: selectedClient.num_meals,
      num_snacks: selectedClient.num_snacks,
      allergies: selectedClient.allergies || [],
      food_preferences: selectedClient.food_preferences || '',
      manual_calories: selectedClient.target_calories || '',
      manual_protein_pct: selectedClient.macro_split?.protein || '',
      manual_carbs_pct: selectedClient.macro_split?.carbohydrates || '',
      manual_fat_pct: selectedClient.macro_split?.fat || '',
      manual_protein_grams: selectedClient.macro_split?.protein_grams || '',
      manual_carbs_grams: selectedClient.macro_split?.carbs_grams || '',
      manual_fat_grams: selectedClient.macro_split?.fat_grams || '',
      bmr_equation: selectedClient.bmr_equation || 'harris_benedict',
      body_fat_pct: selectedClient.body_fat_pct || '',
      protein_per_kg: selectedClient.protein_per_kg || ''
    });
    setEditingClient(true);
  };

  // Auto-calculate macros
  const handleAutoCalculate = async () => {
    const data = editingClient ? editFormData : formData;
    const setData = editingClient ? setEditFormData : setFormData;
    
    if (!data.weight || !data.height || !data.age || !data.gender || !data.activity_level) {
      setError('Please fill in weight, height, age, gender, and activity level first');
      return;
    }
    
    try {
      const response = await axios.post(`${API_URL}/api/calculate-macros`, null, {
        params: {
          weight: parseFloat(data.weight),
          height: parseFloat(data.height),
          age: parseInt(data.age),
          gender: data.gender,
          activity_level: data.activity_level,
          goal_weight: data.goal_weight ? parseFloat(data.goal_weight) : null,
          bmr_equation: data.bmr_equation || 'harris_benedict',
          body_fat_pct: data.body_fat_pct ? parseFloat(data.body_fat_pct) : null,
          protein_per_kg: data.protein_per_kg ? parseFloat(data.protein_per_kg) : null
        }
      });
      
      if (response.data.success) {
        setData(prev => ({
          ...prev,
          manual_calories: response.data.target_calories.toString(),
          manual_protein_pct: response.data.protein_pct.toString(),
          manual_carbs_pct: response.data.carbs_pct.toString(),
          manual_fat_pct: response.data.fat_pct.toString(),
          manual_protein_grams: response.data.protein_grams.toString(),
          manual_carbs_grams: response.data.carbs_grams.toString(),
          manual_fat_grams: response.data.fat_grams.toString()
        }));
        setSuccess(`Recommended: ${response.data.target_calories} kcal | P: ${response.data.protein_grams}g (${response.data.protein_per_kg}g/kg) | C: ${response.data.carbs_grams}g | F: ${response.data.fat_grams}g | Equation: ${(response.data.equation_used || 'harris_benedict').replace('_', '-')}`);
        setTimeout(() => setSuccess(null), 6000);
      }
    } catch (err) {
      setError(parseErrorMessage(err, 'Failed to calculate macros'));
    }
  };

  // Loading state
  if (authLoading) {
    return (
      <div className="app-container">
        <div className="loading-screen">
          <div className="loading-spinner"></div>
          <p>Loading Plate Planner Pro...</p>
        </div>
      </div>
    );
  }

  // Auth screens
  if (!user) {
    return (
      <AuthPage
        authView={authView}
        setAuthView={setAuthView}
        error={error}
        setError={setError}
        success={success}
        setSuccess={setSuccess}
        loading={loading}
        loginForm={loginForm}
        setLoginForm={setLoginForm}
        registerForm={registerForm}
        setRegisterForm={setRegisterForm}
        forgotPasswordEmail={forgotPasswordEmail}
        setForgotPasswordEmail={setForgotPasswordEmail}
        resetPasswordForm={resetPasswordForm}
        setResetPasswordForm={setResetPasswordForm}
        handleLogin={handleLogin}
        handleRegister={handleRegister}
        handleForgotPassword={handleForgotPassword}
        handleResetPassword={handleResetPassword}
      />
    );
  }

  // Check for locked account (payment failed or canceled)
  const isAccountLocked = subscription && ['past_due', 'canceled'].includes(subscription.plan_status);
  
  if (isAccountLocked && !isAdmin()) {
    return (
      <div className="app-container">
        <nav className="navbar">
          <div className="nav-brand">
            <img src="/plate-planner-pro-logo.webp" alt="Logo" className="nav-logo-img" />
            <span>Plate Planner Pro</span>
          </div>
          <div className="nav-user">
            <span className="user-name">{user.name}</span>
            <button className="btn-logout" onClick={handleLogout}>Logout</button>
          </div>
        </nav>
        
        <main className="main-content">
          <div className="locked-account-view">
            <div className="locked-card">
              <div className="locked-icon"><Shield size={20} /></div>
              <h1>Account Locked</h1>
              <p className="locked-reason">
                {subscription.plan_status === 'past_due' 
                  ? 'Your payment failed. Please update your payment method to continue using Plate Planner Pro.'
                  : 'Your subscription has been canceled. Please resubscribe to regain access.'}
              </p>
              
              <div className="locked-status">
                <span className={`status-badge status-${subscription.plan_status}`}>
                  {subscription.plan_status === 'past_due' ? 'Payment Failed' : 'Canceled'}
                </span>
              </div>

              <div className="locked-actions">
                <button 
                  className="btn-primary btn-lg"
                  onClick={() => handleSubscribe(subscription.plan || 'pro')}
                  disabled={loading}
                >
                  {loading ? 'Processing...' : 'Update Payment Method'}
                </button>
              </div>

              <p className="locked-help">
                Need help? Contact support at <a href="mailto:support@plateplannerpro.com">support@plateplannerpro.com</a>
              </p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Main app (authenticated)
  
  // Admin-only view - no meal planning features
  if (isAdmin()) {
    return (
      <AdminDashboard
        user={user}
        error={error}
        success={success}
        adminStats={adminStats}
        adminUsers={adminUsers}
        adminPagination={adminPagination}
        adminFilters={adminFilters}
        setAdminFilters={setAdminFilters}
        adminLoading={adminLoading}
        selectedAdminUser={selectedAdminUser}
        setSelectedAdminUser={setSelectedAdminUser}
        setCurrentView={setCurrentView}
        loadAdminStats={loadAdminStats}
        loadAdminUsers={loadAdminUsers}
        loadAdminUserDetail={loadAdminUserDetail}
        handleAdminUpdatePlan={handleAdminUpdatePlan}
        handleAdminDeleteUser={handleAdminDeleteUser}
            handleAdminDeactivateUser={handleAdminDeactivateUser}
        handleLogout={handleLogout}
        API_URL={API_URL}
      />
    );
  }
  
  // Regular user view  // Regular user view
  return (
    <div className="app-container">
      <Navbar
        currentView={currentView}
        setCurrentView={setCurrentView}
        user={user}
        subscription={subscription}
        teamStatus={teamStatus}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
        isFeatureAvailable={isFeatureAvailable}
        loadClients={loadClients}
        loadTemplates={loadTemplates}
        handleLogout={handleLogout}
      />

      {/* Email Verification Banner */}
      {user && user.email_verified === false && (
        <div className="verification-banner">
          <span><Mail size={16} style={{marginRight: '6px'}} />Please verify your email address to access all features.</span>
          <button 
            className="btn-sm btn-link"
            onClick={handleResendVerification}
            disabled={loading}
          >
            {loading ? 'Sending...' : 'Resend verification email'}
          </button>
        </div>
      )}

      <main className="main-content">
        {error && <div className="alert alert-error" style={{marginBottom: '16px'}}>{error}</div>}
        {success && <div className="alert alert-success" style={{marginBottom: '16px'}}>{success}</div>}

        {/* Home View */}
        {currentView === 'home' && (
          <div className="home-view">
            <div className="hero-section animate-fade-in">
              <h1 className="hero-title">
                {clientsCount > 0 ? 'Welcome back' : 'Welcome'}, {user.name?.split(' ')[0]}!
              </h1>
              <p className="hero-subtitle">
                {clientsCount > 0 
                  ? 'Create calorie and macro-controlled meal plans for your fitness clients'
                  : 'Get started by adding your first client to create personalized meal plans'
                }
              </p>
              <div className="hero-buttons">
                <button className="btn-primary btn-glow" onClick={() => setCurrentView('add-client')} data-testid="add-new-client-btn">
                  <UserPlus size={18} style={{marginRight: '8px'}} />
                  {clientsCount > 0 ? 'Add New Client' : 'Add Your First Client'}
                </button>
                {clientsCount > 0 && (
                  <button className="btn-secondary" onClick={() => { setCurrentView('clients'); loadClients(); }} data-testid="view-clients-btn">
                    <Users size={18} style={{marginRight: '8px'}} />
                    View Clients
                  </button>
                )}
              </div>
            </div>

            {/* Stats Overview */}
            <div className="stats-overview">
              <div className="stat-card animate-slide-up" style={{animationDelay: '0.1s'}}>
                <div className="stat-icon-wrapper stat-icon-clients">
                  <Users size={24} strokeWidth={2} />
                </div>
                <div className="stat-content">
                  <span className="stat-label">Total Clients</span>
                  <span className="stat-value">{clientsCount}</span>
                  <span className="stat-sublabel">
                    {subscription?.max_clients ? `of ${subscription.max_clients} max` : 'unlimited'}
                  </span>
                </div>
              </div>
              
              <div className="stat-card animate-slide-up" style={{animationDelay: '0.15s'}}>
                <div className="stat-icon-wrapper stat-icon-plans">
                  <UtensilsCrossed size={24} strokeWidth={2} />
                </div>
                <div className="stat-content">
                  <span className="stat-label">Meal Plans</span>
                  <span className="stat-value">{recentMealPlans.length}</span>
                  <span className="stat-sublabel">created recently</span>
                </div>
              </div>
              
              <div className="stat-card animate-slide-up" style={{animationDelay: '0.2s'}}>
                <div className="stat-icon-wrapper stat-icon-subscription">
                  {subscription?.plan === 'unlimited' ? <Crown size={24} strokeWidth={2} /> : 
                   subscription?.plan === 'pro' ? <Sparkles size={24} strokeWidth={2} /> : 
                   <Target size={24} strokeWidth={2} />}
                </div>
                <div className="stat-content">
                  <span className="stat-label">Current Plan</span>
                  <span className="stat-value plan-badge">{subscription?.plan?.charAt(0).toUpperCase() + subscription?.plan?.slice(1) || 'Free'}</span>
                  <span className="stat-sublabel">
                    {subscription?.plan_status === 'trialing' ? `${subscription.trial_days_remaining} days left` : 'active'}
                  </span>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="quick-actions-section">
              <h2 className="section-title">Quick Actions</h2>
              <div className="features-grid">
                <div className="feature-card glass-card animate-slide-up" style={{animationDelay: '0.1s'}} onClick={() => { setCurrentView('clients'); loadClients(); }} data-testid="client-profiles-card">
                  <div className="feature-icon-wrapper">
                    <Users size={28} strokeWidth={1.5} className="feature-lucide-icon" />
                  </div>
                  <h3>Client Profiles</h3>
                  <p>Comprehensive intake forms with BMR and TDEE calculations</p>
                  <div className="card-action-hint">
                    <ChevronRight size={16} />
                  </div>
                  <div className="card-shine"></div>
                </div>
                {isFeatureAvailable('quick_meal_plan') && (
                <div className="feature-card glass-card animate-slide-up" style={{animationDelay: '0.2s'}} onClick={() => setCurrentView('quick-meal-plan')} data-testid="quick-meal-plan-card">
                  <div className="feature-icon-wrapper">
                    <Zap size={28} strokeWidth={1.5} className="feature-lucide-icon" />
                  </div>
                  <h3>Quick Meal Plan</h3>
                  <p>Generate a one-off meal plan without saving a client</p>
                  <div className="card-action-hint">
                    <ChevronRight size={16} />
                  </div>
                  <div className="card-shine"></div>
                </div>
                )}
                <div className="feature-card glass-card active-clients-card animate-slide-up" style={{animationDelay: '0.3s'}} onClick={() => { setCurrentView('clients'); loadClients(); }} data-testid="active-clients-card">
                  <div className="feature-icon-wrapper">
                    <BarChart3 size={28} strokeWidth={1.5} className="feature-lucide-icon" />
                  </div>
                  <h3>Active Clients</h3>
                  <AnimatedCounter value={clientsCount} />
                  <p className="client-count-label">
                    {subscription?.max_clients ? `of ${subscription.max_clients} clients` : 'unlimited'}
                  </p>
                  <div className="card-action-hint">
                    <ChevronRight size={16} />
                  </div>
                  <div className="card-shine"></div>
                </div>
              </div>
            </div>

            {/* Recent Meal Plans */}
            {recentMealPlans.length > 0 && (
              <div className="recent-plans-section" data-testid="recent-plans-section">
                <h2 className="section-title">Recent Meal Plans</h2>
                <div className="recent-plans-list">
                  {recentMealPlans.map((plan) => (
                    <div 
                      key={plan.id} 
                      className="recent-plan-card"
                      onClick={async () => {
                        if (plan.client_id) {
                          const res = await axios.get(`${API_URL}/api/meal-plans/${plan.id}`);
                          setSelectedMealPlan(res.data);
                          setCurrentView('meal-plan-detail');
                        }
                      }}
                      data-testid={`recent-plan-${plan.id}`}
                    >
                      <div className="recent-plan-icon-wrapper">
                        <UtensilsCrossed size={20} strokeWidth={1.5} />
                      </div>
                      <div className="recent-plan-info">
                        <h4>{plan.client_name}</h4>
                        <p>{plan.target_calories ? `${plan.target_calories} kcal/day` : 'Custom plan'}</p>
                        <span className="recent-plan-date">
                          <Clock size={12} style={{marginRight: '4px'}} />
                          {plan.created_at ? new Date(plan.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : 'Recent'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Trial/Upgrade Banner */}
            {subscription?.plan_status === 'trialing' && (
              <div className="trial-banner">
                <div className="trial-info">
                  <h3><PartyPopper size={20} style={{marginRight: '8px'}} />You're on your 7-day Pro trial!</h3>
                  <p>{subscription.trial_days_remaining} days remaining. Subscribe now to keep all Pro features.</p>
                </div>
                <button className="btn-primary" onClick={() => setCurrentView('billing')}>
                  View Plans
                </button>
              </div>
            )}
          </div>
        )}

        {/* Billing View */}
        {currentView === 'billing' && (
          <BillingPage
            subscription={subscription}
            loading={loading}
            setCurrentView={setCurrentView}
            handleSubscribe={handleSubscribe}
            handleCancelSubscription={handleCancelSubscription}
          />
        )}

        {/* Settings View */}
        {currentView === 'settings' && (
          <SettingsPage
            user={user}
            subscription={subscription}
            loading={loading}
            uploadingLogo={uploadingLogo}
            brandingForm={brandingForm}
            setBrandingForm={setBrandingForm}
            teamMembers={teamMembers}
            teamStatus={teamStatus}
            setCurrentView={setCurrentView}
            setShowInviteModal={setShowInviteModal}
            handleLogoUpload={handleLogoUpload}
            handleSaveBranding={handleSaveBranding}
            handleRemoveTeamMember={handleRemoveTeamMember}
            handleDeleteAccount={handleDeleteAccount}
            isFeatureAvailable={isFeatureAvailable}
            API_URL={API_URL}
          />
        )}

        {/* Clients List View */}
        {currentView === 'clients' && (
          <div className="clients-view">
            <div className="page-header">
              <h2>Your Clients</h2>
              <button className="btn-primary" onClick={() => setCurrentView('add-client')}><Plus size={16} /> Add Client</button>
            </div>

            {loading ? (
              <div className="clients-grid">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="client-card skeleton-card">
                    <div className="skeleton skeleton-avatar"></div>
                    <div className="client-info">
                      <div className="skeleton skeleton-text skeleton-title"></div>
                      <div className="skeleton skeleton-text skeleton-subtitle"></div>
                      <div className="skeleton skeleton-text skeleton-meta"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : clients.length === 0 ? (
              <div className="empty-state">
                <p>No clients yet. Add your first client to get started!</p>
                <button className="btn-primary" onClick={() => setCurrentView('add-client')}><UserPlus size={16} /> Add Client</button>
              </div>
            ) : (
              <div className="clients-grid">
                {clients.map((client, index) => {
                  // Generate consistent color based on name
                  const colors = [
                    ['#3b82f6', '#1d4ed8'], // blue
                    ['#10b981', '#059669'], // green
                    ['#8b5cf6', '#6d28d9'], // purple
                    ['#f59e0b', '#d97706'], // amber
                    ['#ef4444', '#dc2626'], // red
                    ['#ec4899', '#db2777'], // pink
                    ['#06b6d4', '#0891b2'], // cyan
                  ];
                  const colorIndex = (client.first_name.charCodeAt(0) + client.last_name.charCodeAt(0)) % colors.length;
                  const [colorStart, colorEnd] = colors[colorIndex];
                  
                  return (
                    <div 
                      key={client.id} 
                      className="client-card"
                      onClick={() => {
                        setSelectedClient(client);
                        loadMealPlans(client.id);
                        loadWeightHistory(client.id);
                        setCurrentView('client-detail');
                      }}
                      style={{'--card-delay': `${index * 0.05}s`}}
                      data-testid={`client-card-${client.id}`}
                    >
                      <div 
                        className="client-avatar"
                        style={{background: `linear-gradient(135deg, ${colorStart} 0%, ${colorEnd} 100%)`}}
                      >
                        {client.first_name[0]}{client.last_name[0]}
                      </div>
                      <div className="client-info">
                        <div className="client-name-row">
                          <h3>{client.first_name} {client.last_name}</h3>
                          {client.weight_history && client.weight_history.length > 1 && (
                            <span className="client-status-badge active" title="Weight tracking active">
                              <TrendingUp size={12} />
                            </span>
                          )}
                        </div>
                        {client.email && <p className="client-email">{client.email}</p>}
                        <div className="client-meta">
                          <span className="meta-item">
                            <Flame size={12} />
                            {client.target_calories} kcal
                          </span>
                          <span className="meta-item">
                            <Target size={12} />
                            {client.goal_weight}kg goal
                          </span>
                        </div>
                        <div className="client-macros">
                          <span className="macro-pill protein">P:{client.macro_split?.protein_grams || Math.round((client.macro_split?.protein || 0) / 100 * client.target_calories / 4)}g ({client.macro_split?.protein}%)</span>
                          <span className="macro-pill carbs">C:{client.macro_split?.carbs_grams || Math.round((client.macro_split?.carbohydrates || 0) / 100 * client.target_calories / 4)}g ({client.macro_split?.carbohydrates}%)</span>
                          <span className="macro-pill fat">F:{client.macro_split?.fat_grams || Math.round((client.macro_split?.fat || 0) / 100 * client.target_calories / 9)}g ({client.macro_split?.fat}%)</span>
                        </div>
                      </div>
                      <div className="client-card-arrow">
                        <ChevronRight size={20} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Add Client View */}
        {currentView === 'add-client' && (
          <div className="add-client-view">
            <div className="page-header">
              <h2>Add New Client</h2>
              <button className="btn-back" onClick={() => setCurrentView('home')}><ArrowLeft size={16} /> Back</button>
            </div>

            <form onSubmit={handleCreateClient} className="client-form">
              <div className="form-section">
                <h3>Personal Information</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>First Name *</label>
                    <input
                      type="text"
                      name="first_name"
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Last Name *</label>
                    <input
                      type="text"
                      name="last_name"
                      value={formData.last_name}
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Email <span className="optional-label">(optional)</span></label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="Optional"
                    />
                  </div>
                  <div className="form-group">
                    <label>Phone <span className="optional-label">(optional)</span></label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="Optional"
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3>Physical Details</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>Age *</label>
                    <input
                      type="number"
                      name="age"
                      value={formData.age}
                      onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                      required
                      min="16"
                      max="100"
                    />
                  </div>
                  <div className="form-group">
                    <label>Gender *</label>
                    <select
                      name="gender"
                      value={formData.gender}
                      onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                      required
                    >
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Height (cm) *</label>
                    <input
                      type="number"
                      name="height"
                      value={formData.height}
                      onChange={(e) => setFormData({ ...formData, height: e.target.value })}
                      required
                      min="100"
                      max="250"
                    />
                  </div>
                  <div className="form-group">
                    <label>Weight (kg) *</label>
                    <input
                      type="number"
                      name="weight"
                      value={formData.weight}
                      onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                      required
                      min="30"
                      max="300"
                      step="0.1"
                    />
                  </div>
                  <div className="form-group">
                    <label>Goal Weight (kg)</label>
                    <input
                      type="number"
                      name="goal_weight"
                      value={formData.goal_weight}
                      onChange={(e) => setFormData({ ...formData, goal_weight: e.target.value })}
                      min="30"
                      max="300"
                      step="0.1"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Activity Level *</label>
                  <select
                    name="activity_level"
                    value={formData.activity_level}
                    onChange={(e) => setFormData({ ...formData, activity_level: e.target.value })}
                    required
                  >
                    <option value="sedentary">Sedentary (little or no exercise)</option>
                    <option value="lightly_active">Lightly Active (1-3 days/week)</option>
                    <option value="moderately_active">Moderately Active (3-5 days/week)</option>
                    <option value="very_active">Very Active (6-7 days/week)</option>
                    <option value="extra_active">Extra Active (physical job + training)</option>
                  </select>
                </div>
              </div>

              <div className="form-section">
                <h3>Meal Preferences</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>Meals per Day</label>
                    <select
                      name="num_meals"
                      value={formData.num_meals}
                      onChange={(e) => setFormData({ ...formData, num_meals: parseInt(e.target.value) })}
                    >
                      {[2, 3, 4, 5, 6].map(n => (
                        <option key={n} value={n}>{n} meals</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Snacks per Day</label>
                    <select
                      name="num_snacks"
                      value={formData.num_snacks}
                      onChange={(e) => setFormData({ ...formData, num_snacks: parseInt(e.target.value) })}
                    >
                      {[0, 1, 2, 3, 4].map(n => (
                        <option key={n} value={n}>{n} snacks</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label>Dietary Restrictions</label>
                  <input
                    type="text"
                    placeholder="e.g., vegetarian, vegan, gluten-free (comma separated)"
                    value={Array.isArray(formData.dietary_restrictions) ? formData.dietary_restrictions.join(', ') : formData.dietary_restrictions}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      dietary_restrictions: e.target.value
                    })}
                    onBlur={(e) => setFormData({
                      ...formData,
                      dietary_restrictions: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                    })}
                  />
                </div>
                <div className="form-group">
                  <label>Allergies</label>
                  <input
                    type="text"
                    placeholder="e.g., nuts, dairy, shellfish (comma separated)"
                    value={Array.isArray(formData.allergies) ? formData.allergies.join(', ') : formData.allergies}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      allergies: e.target.value
                    })}
                    onBlur={(e) => setFormData({
                      ...formData,
                      allergies: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                    })}
                  />
                </div>
              </div>

              <div className="form-section">
                <h3>Calorie & Macro Targets</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>BMR Equation</label>
                    <select
                      data-testid="bmr-equation-select"
                      value={formData.bmr_equation}
                      onChange={(e) => setFormData({ ...formData, bmr_equation: e.target.value })}
                    >
                      <option value="harris_benedict">Harris-Benedict (Revised)</option>
                      <option value="mifflin_st_jeor">Mifflin-St Jeor</option>
                      <option value="cunningham">Cunningham</option>
                      <option value="katch_mcardle">Katch-McArdle</option>
                    </select>
                  </div>
                  {(formData.bmr_equation === 'cunningham' || formData.bmr_equation === 'katch_mcardle') && (
                    <div className="form-group">
                      <label>Body Fat % <span style={{fontSize:'11px',color:'#888'}}>(optional, improves accuracy)</span></label>
                      <input
                        type="number"
                        data-testid="body-fat-input"
                        value={formData.body_fat_pct}
                        onChange={(e) => setFormData({ ...formData, body_fat_pct: e.target.value })}
                        placeholder="e.g., 15"
                        min="3"
                        max="50"
                        step="0.1"
                      />
                    </div>
                  )}
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Protein per kg <span style={{fontSize:'11px',color:'#888'}}>(g/kg target)</span></label>
                    <input
                      type="number"
                      data-testid="protein-per-kg-input"
                      value={formData.protein_per_kg}
                      onChange={(e) => setFormData({ ...formData, protein_per_kg: e.target.value })}
                      placeholder="e.g., 2.0"
                      min="0.8"
                      max="3.5"
                      step="0.1"
                    />
                  </div>
                  <div className="form-group" style={{flex:2}}>
                    <label>&nbsp;</label>
                    <button type="button" className="btn-secondary" onClick={handleAutoCalculate} data-testid="auto-calculate-btn">
                      <Calculator size={16} /> Auto-Calculate
                    </button>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Daily Calories</label>
                    <input
                      type="number"
                      data-testid="manual-calories-input"
                      name="manual_calories"
                      value={formData.manual_calories}
                      onChange={(e) => setFormData({ ...formData, manual_calories: e.target.value })}
                      placeholder="Auto-calculated if blank"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Protein (g){formData.manual_protein_pct ? <span style={{fontSize:'11px',color:'#888',marginLeft:'6px'}}>{formData.manual_protein_pct}%</span> : ''}</label>
                    <input
                      type="number"
                      data-testid="protein-grams-input"
                      value={formData.manual_protein_grams}
                      onChange={(e) => {
                        const grams = e.target.value;
                        const cals = parseInt(formData.manual_calories) || 2000;
                        const pct = grams ? Math.round((parseInt(grams) * 4 / cals) * 100) : '';
                        setFormData({ ...formData, manual_protein_grams: grams, manual_protein_pct: pct.toString() });
                      }}
                      placeholder="e.g., 150"
                    />
                  </div>
                  <div className="form-group">
                    <label>Carbs (g){formData.manual_carbs_pct ? <span style={{fontSize:'11px',color:'#888',marginLeft:'6px'}}>{formData.manual_carbs_pct}%</span> : ''}</label>
                    <input
                      type="number"
                      data-testid="carbs-grams-input"
                      value={formData.manual_carbs_grams}
                      onChange={(e) => {
                        const grams = e.target.value;
                        const cals = parseInt(formData.manual_calories) || 2000;
                        const pct = grams ? Math.round((parseInt(grams) * 4 / cals) * 100) : '';
                        setFormData({ ...formData, manual_carbs_grams: grams, manual_carbs_pct: pct.toString() });
                      }}
                      placeholder="e.g., 200"
                    />
                  </div>
                  <div className="form-group">
                    <label>Fat (g){formData.manual_fat_pct ? <span style={{fontSize:'11px',color:'#888',marginLeft:'6px'}}>{formData.manual_fat_pct}%</span> : ''}</label>
                    <input
                      type="number"
                      data-testid="fat-grams-input"
                      value={formData.manual_fat_grams}
                      onChange={(e) => {
                        const grams = e.target.value;
                        const cals = parseInt(formData.manual_calories) || 2000;
                        const pct = grams ? Math.round((parseInt(grams) * 9 / cals) * 100) : '';
                        setFormData({ ...formData, manual_fat_grams: grams, manual_fat_pct: pct.toString() });
                      }}
                      placeholder="e.g., 65"
                    />
                  </div>
                </div>
              </div>

              <button type="submit" className="btn-primary btn-full" disabled={loading}>
                {loading ? 'Creating...' : 'Create Client Profile'}
              </button>
            </form>
          </div>
        )}

        {/* Client Detail View */}
        {currentView === 'client-detail' && selectedClient && (
          <div className="client-detail-view">
            <div className="page-header">
              <h2>{selectedClient.first_name} {selectedClient.last_name}</h2>
              <div className="header-actions">
                <button className="btn-secondary btn-with-icon" onClick={handleEditClient}><Edit size={16} /> Edit</button>
                <button className="btn-danger btn-with-icon" onClick={() => handleDeleteClient(selectedClient.id)}><Trash2 size={16} /> Delete</button>
                <button className="btn-back btn-with-icon" onClick={() => { setCurrentView('clients'); loadClients(); }}><ArrowLeft size={16} /> Back</button>
              </div>
            </div>

            {editingClient ? (
              <form onSubmit={handleUpdateClient} className="client-form">
                {/* Edit form - same structure as add form but with editFormData */}
                <div className="form-section">
                  <h3>Personal Information</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label>First Name *</label>
                      <input
                        type="text"
                        value={editFormData.first_name}
                        onChange={(e) => setEditFormData({ ...editFormData, first_name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Last Name *</label>
                      <input
                        type="text"
                        value={editFormData.last_name}
                        onChange={(e) => setEditFormData({ ...editFormData, last_name: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Email <span className="optional-label">(optional)</span></label>
                      <input
                        type="email"
                        value={editFormData.email}
                        onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                        placeholder="Optional"
                      />
                    </div>
                    <div className="form-group">
                      <label>Phone <span className="optional-label">(optional)</span></label>
                      <input
                        type="tel"
                        value={editFormData.phone}
                        onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
                        placeholder="Optional"
                      />
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h3>Physical Details</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Age *</label>
                      <input
                        type="number"
                        value={editFormData.age}
                        onChange={(e) => setEditFormData({ ...editFormData, age: e.target.value })}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Gender *</label>
                      <select
                        value={editFormData.gender}
                        onChange={(e) => setEditFormData({ ...editFormData, gender: e.target.value })}
                        required
                      >
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Height (cm) *</label>
                      <input
                        type="number"
                        value={editFormData.height}
                        onChange={(e) => setEditFormData({ ...editFormData, height: e.target.value })}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Weight (kg) *</label>
                      <input
                        type="number"
                        value={editFormData.weight}
                        onChange={(e) => setEditFormData({ ...editFormData, weight: e.target.value })}
                        required
                        step="0.1"
                      />
                    </div>
                    <div className="form-group">
                      <label>Goal Weight (kg)</label>
                      <input
                        type="number"
                        value={editFormData.goal_weight}
                        onChange={(e) => setEditFormData({ ...editFormData, goal_weight: e.target.value })}
                        step="0.1"
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Activity Level *</label>
                    <select
                      value={editFormData.activity_level}
                      onChange={(e) => setEditFormData({ ...editFormData, activity_level: e.target.value })}
                      required
                    >
                      <option value="sedentary">Sedentary</option>
                      <option value="lightly_active">Lightly Active</option>
                      <option value="moderately_active">Moderately Active</option>
                      <option value="very_active">Very Active</option>
                      <option value="extra_active">Extra Active</option>
                    </select>
                  </div>
                </div>

                <div className="form-section">
                  <h3>Meal Preferences</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Meals per Day</label>
                      <select
                        value={editFormData.num_meals}
                        onChange={(e) => setEditFormData({ ...editFormData, num_meals: parseInt(e.target.value) })}
                      >
                        {[2, 3, 4, 5, 6].map(n => (
                          <option key={n} value={n}>{n} meals</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Snacks per Day</label>
                      <select
                        value={editFormData.num_snacks}
                        onChange={(e) => setEditFormData({ ...editFormData, num_snacks: parseInt(e.target.value) })}
                      >
                        {[0, 1, 2, 3, 4].map(n => (
                          <option key={n} value={n}>{n} snacks</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Dietary Restrictions</label>
                    <input
                      type="text"
                      placeholder="e.g., vegetarian, vegan, gluten-free (comma separated)"
                      value={Array.isArray(editFormData.dietary_restrictions) ? editFormData.dietary_restrictions.join(', ') : editFormData.dietary_restrictions}
                      onChange={(e) => setEditFormData({ 
                        ...editFormData, 
                        dietary_restrictions: e.target.value
                      })}
                      onBlur={(e) => setEditFormData({
                        ...editFormData,
                        dietary_restrictions: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                      })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Allergies</label>
                    <input
                      type="text"
                      placeholder="e.g., nuts, dairy, shellfish (comma separated)"
                      value={Array.isArray(editFormData.allergies) ? editFormData.allergies.join(', ') : editFormData.allergies}
                      onChange={(e) => setEditFormData({ 
                        ...editFormData, 
                        allergies: e.target.value
                      })}
                      onBlur={(e) => setEditFormData({
                        ...editFormData,
                        allergies: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                      })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Food Preferences</label>
                    <textarea
                      placeholder="e.g., prefers chicken over beef, enjoys Mediterranean cuisine, dislikes spicy food..."
                      value={editFormData.food_preferences || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, food_preferences: e.target.value })}
                      rows={3}
                    />
                  </div>
                </div>

                <div className="form-section">
                  <h3>Calorie & Macro Targets</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label>BMR Equation</label>
                      <select
                        data-testid="edit-bmr-equation-select"
                        value={editFormData.bmr_equation || 'harris_benedict'}
                        onChange={(e) => setEditFormData({ ...editFormData, bmr_equation: e.target.value })}
                      >
                        <option value="harris_benedict">Harris-Benedict (Revised)</option>
                        <option value="mifflin_st_jeor">Mifflin-St Jeor</option>
                        <option value="cunningham">Cunningham</option>
                        <option value="katch_mcardle">Katch-McArdle</option>
                      </select>
                    </div>
                    {(editFormData.bmr_equation === 'cunningham' || editFormData.bmr_equation === 'katch_mcardle') && (
                      <div className="form-group">
                        <label>Body Fat % <span style={{fontSize:'11px',color:'#888'}}>(optional)</span></label>
                        <input
                          type="number"
                          data-testid="edit-body-fat-input"
                          value={editFormData.body_fat_pct}
                          onChange={(e) => setEditFormData({ ...editFormData, body_fat_pct: e.target.value })}
                          placeholder="e.g., 15"
                          min="3"
                          max="50"
                          step="0.1"
                        />
                      </div>
                    )}
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Protein per kg <span style={{fontSize:'11px',color:'#888'}}>(g/kg target)</span></label>
                      <input
                        type="number"
                        data-testid="edit-protein-per-kg-input"
                        value={editFormData.protein_per_kg}
                        onChange={(e) => setEditFormData({ ...editFormData, protein_per_kg: e.target.value })}
                        placeholder="e.g., 2.0"
                        min="0.8"
                        max="3.5"
                        step="0.1"
                      />
                    </div>
                    <div className="form-group" style={{flex:2}}>
                      <label>&nbsp;</label>
                      <button type="button" className="btn-secondary" onClick={handleAutoCalculate} data-testid="edit-auto-calculate-btn">
                        <Calculator size={16} /> Auto-Calculate
                      </button>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Daily Calories</label>
                      <input
                        type="number"
                        data-testid="edit-manual-calories-input"
                        value={editFormData.manual_calories}
                        onChange={(e) => setEditFormData({ ...editFormData, manual_calories: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Protein (g){editFormData.manual_protein_pct ? <span style={{fontSize:'11px',color:'#888',marginLeft:'6px'}}>{editFormData.manual_protein_pct}%</span> : ''}</label>
                      <input
                        type="number"
                        data-testid="edit-protein-grams-input"
                        value={editFormData.manual_protein_grams}
                        onChange={(e) => {
                          const grams = e.target.value;
                          const cals = parseInt(editFormData.manual_calories) || 2000;
                          const pct = grams ? Math.round((parseInt(grams) * 4 / cals) * 100) : '';
                          setEditFormData({ ...editFormData, manual_protein_grams: grams, manual_protein_pct: pct.toString() });
                        }}
                        placeholder="e.g., 150"
                      />
                    </div>
                    <div className="form-group">
                      <label>Carbs (g){editFormData.manual_carbs_pct ? <span style={{fontSize:'11px',color:'#888',marginLeft:'6px'}}>{editFormData.manual_carbs_pct}%</span> : ''}</label>
                      <input
                        type="number"
                        data-testid="edit-carbs-grams-input"
                        value={editFormData.manual_carbs_grams}
                        onChange={(e) => {
                          const grams = e.target.value;
                          const cals = parseInt(editFormData.manual_calories) || 2000;
                          const pct = grams ? Math.round((parseInt(grams) * 4 / cals) * 100) : '';
                          setEditFormData({ ...editFormData, manual_carbs_grams: grams, manual_carbs_pct: pct.toString() });
                        }}
                        placeholder="e.g., 200"
                      />
                    </div>
                    <div className="form-group">
                      <label>Fat (g){editFormData.manual_fat_pct ? <span style={{fontSize:'11px',color:'#888',marginLeft:'6px'}}>{editFormData.manual_fat_pct}%</span> : ''}</label>
                      <input
                        type="number"
                        data-testid="edit-fat-grams-input"
                        value={editFormData.manual_fat_grams}
                        onChange={(e) => {
                          const grams = e.target.value;
                          const cals = parseInt(editFormData.manual_calories) || 2000;
                          const pct = grams ? Math.round((parseInt(grams) * 9 / cals) * 100) : '';
                          setEditFormData({ ...editFormData, manual_fat_grams: grams, manual_fat_pct: pct.toString() });
                        }}
                        placeholder="e.g., 65"
                      />
                    </div>
                  </div>
                </div>

                <div className="form-actions">
                  <button type="button" className="btn-secondary" onClick={() => { setEditingClient(false); setEditFormData(null); }}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary" disabled={loading}>
                    {loading ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            ) : (
              <>
                <div className="client-summary">
                  <div className="summary-grid">
                    <div className="summary-item">
                      <span className="label">Email</span>
                      <span className="value">{selectedClient.email}</span>
                    </div>
                    <div className="summary-item">
                      <span className="label">Age / Gender</span>
                      <span className="value">{selectedClient.age} / {selectedClient.gender}</span>
                    </div>
                    <div className="summary-item">
                      <span className="label">Height</span>
                      <span className="value">{selectedClient.height} cm</span>
                    </div>
                    <div className="summary-item">
                      <span className="label">Current Weight</span>
                      <span className="value">{selectedClient.weight} kg</span>
                    </div>
                    <div className="summary-item">
                      <span className="label">Goal Weight</span>
                      <span className="value">{selectedClient.goal_weight || 'Not set'} kg</span>
                    </div>
                    <div className="summary-item">
                      <span className="label">Daily Calories</span>
                      <span className="value">{selectedClient.target_calories} kcal</span>
                    </div>
                    <div className="summary-item">
                      <span className="label">Macros</span>
                      <span className="value">
                        P: {selectedClient.macro_split?.protein_grams || Math.round((selectedClient.macro_split?.protein || 0) / 100 * selectedClient.target_calories / 4)}g ({selectedClient.macro_split?.protein}%) | 
                        C: {selectedClient.macro_split?.carbs_grams || Math.round((selectedClient.macro_split?.carbohydrates || 0) / 100 * selectedClient.target_calories / 4)}g ({selectedClient.macro_split?.carbohydrates}%) | 
                        F: {selectedClient.macro_split?.fat_grams || Math.round((selectedClient.macro_split?.fat || 0) / 100 * selectedClient.target_calories / 9)}g ({selectedClient.macro_split?.fat}%)
                      </span>
                    </div>
                    {selectedClient.bmr_equation && (
                      <div className="summary-item">
                        <span className="label">BMR Equation</span>
                        <span className="value">{selectedClient.bmr_equation.replace(/_/g, '-').replace(/\b\w/g, c => c.toUpperCase())}</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Macro Breakdown Pie Chart */}
                  {selectedClient.macro_split && (
                    <div className="macro-chart-section">
                      <h4>Macro Breakdown</h4>
                      <MacroPieChart 
                        protein={selectedClient.macro_split.protein || 0}
                        carbs={selectedClient.macro_split.carbohydrates || 0}
                        fat={selectedClient.macro_split.fat || 0}
                        proteinGrams={selectedClient.macro_split.protein_grams}
                        carbsGrams={selectedClient.macro_split.carbs_grams}
                        fatGrams={selectedClient.macro_split.fat_grams}
                        calories={selectedClient.target_calories}
                        size={140}
                      />
                    </div>
                  )}
                </div>

                {/* Weight Progress Chart */}
                {weightHistory.length > 0 && (
                  <div className="weight-chart-section">
                    <div className="section-header">
                      <h3>Weight Progress</h3>
                      <button className="btn-secondary btn-sm" onClick={() => setShowAddWeight(true)}>
                        + Add Weight
                      </button>
                    </div>
                    <div className="chart-container">
                      <ResponsiveContainer width="100%" height={250}>
                        <LineChart data={weightHistory}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" />
                          <YAxis domain={['dataMin - 2', 'dataMax + 2']} />
                          <Tooltip />
                          {selectedClient.goal_weight && (
                            <ReferenceLine y={selectedClient.goal_weight} stroke="#10B981" strokeDasharray="5 5" label="Goal" />
                          )}
                          <Line type="monotone" dataKey="weight" stroke="#021b79" strokeWidth={2} dot={{ fill: '#021b79' }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}

                {/* Generate Meal Plan */}
                <div className="generate-section">
                  <h3>Generate Meal Plan</h3>
                  <div className="meal-plan-options">
                    <div className="form-group">
                      <label>Template Style</label>
                      <select
                        value={mealPlanOptions.template_option}
                        onChange={(e) => setMealPlanOptions({ ...mealPlanOptions, template_option: parseInt(e.target.value) })}
                      >
                        <option value={1}>Classic & Traditional</option>
                        <option value={2}>Quick & Simple</option>
                        <option value={3}>Variety & Gourmet</option>
                      </select>
                    </div>
                    <div className={`form-group feature-option ${!isFeatureAvailable('calorie_cycling') ? 'feature-locked' : ''}`}>
                      <label>
                        <input
                          type="checkbox"
                          checked={mealPlanOptions.calorie_cycling}
                          onChange={(e) => {
                            if (isFeatureAvailable('calorie_cycling')) {
                              setMealPlanOptions({ ...mealPlanOptions, calorie_cycling: e.target.checked });
                            }
                          }}
                          disabled={!isFeatureAvailable('calorie_cycling')}
                        />
                        Enable Calorie Cycling
                        {!isFeatureAvailable('calorie_cycling') && (
                          <span className="upgrade-badge" onClick={() => setCurrentView('billing')}>
                            <Lock size={12} /> Upgrade to Pro
                          </span>
                        )}
                      </label>
                      {!isFeatureAvailable('calorie_cycling') && (
                        <p className="feature-hint">Vary daily calories for better results</p>
                      )}
                    </div>
                  </div>
                  <button 
                    className="btn-primary"
                    onClick={() => handleGenerateMealPlan(selectedClient.id)}
                    disabled={loading || generatingMealPlan}
                  >
                    <UtensilsCrossed size={16} />
                    {generatingMealPlan ? 'Generating...' : 'Generate Meal Plan'}
                  </button>
                  
                  {/* Generation Progress Overlay */}
                  {generatingMealPlan && (
                    <div className="generation-progress-card">
                      <div className="generation-header">
                        <span className="generation-icon"><Brain size={20} /></span>
                        <span className="generation-title">Plate Planner Pro is crafting your meal plan</span>
                      </div>
                      <div className="generation-step">{generationStep}</div>
                      <div className="progress-bar-container">
                        <div 
                          className="progress-bar-fill" 
                          style={{ width: `${generationProgress}%` }}
                        />
                      </div>
                      <div className="progress-percentage">{generationProgress}%</div>
                      <div className="generation-tip">
                        <Lightbulb size={14} style={{marginRight: '4px'}} />This usually takes 30-60 seconds
                      </div>
                    </div>
                  )}
                </div>

                {/* Existing Meal Plans */}
                {mealPlans.length > 0 && (
                  <div className="meal-plans-section">
                    <h3>Previous Meal Plans</h3>
                    <div className="meal-plans-list">
                      {mealPlans.map(plan => (
                        <div key={plan.id} className="meal-plan-item">
                          <div className="plan-info">
                            <span className="plan-date">
                              {new Date(plan.created_at).toLocaleDateString()}
                            </span>
                            <span className="plan-calories">{plan.macro_totals?.calories} kcal</span>
                          </div>
                          <div className="plan-actions">
                            <button 
                              className="btn-secondary btn-sm"
                              onClick={() => {
                                setSelectedMealPlan(plan);
                                setCurrentView('meal-plan-detail');
                              }}
                            >
                              <Eye size={14} /> View
                            </button>
                            <button 
                              className="btn-secondary btn-sm"
                              onClick={() => handleExportPDF(plan.id)}
                            >
                              <FileText size={14} /> PDF
                            </button>
                            <button 
                              className="btn-danger btn-sm btn-icon-only"
                              onClick={() => handleDeleteMealPlan(plan.id)}
                              title="Delete meal plan"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Add Weight Modal */}
            {showAddWeight && (
              <div className="modal-overlay" onClick={() => setShowAddWeight(false)}>
                <div className="modal" onClick={(e) => e.stopPropagation()}>
                  <h3>Add Weight Entry</h3>
                  <div className="form-group">
                    <label>Weight (kg)</label>
                    <input
                      type="number"
                      value={newWeight.weight}
                      onChange={(e) => setNewWeight({ ...newWeight, weight: e.target.value })}
                      step="0.1"
                      autoFocus
                    />
                  </div>
                  <div className="form-group">
                    <label>Note (optional)</label>
                    <input
                      type="text"
                      value={newWeight.note}
                      onChange={(e) => setNewWeight({ ...newWeight, note: e.target.value })}
                      placeholder="e.g., Post holiday"
                    />
                  </div>
                  <div className="modal-actions">
                    <button className="btn-secondary" onClick={() => setShowAddWeight(false)}>Cancel</button>
                    <button className="btn-primary" onClick={handleAddWeight} disabled={loading || !newWeight.weight}>
                      {loading ? 'Saving...' : 'Add Entry'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Meal Plan Detail View */}
        {currentView === 'meal-plan-detail' && selectedMealPlan && (
          <div className="meal-plan-detail-view">
            <div className="page-header">
              <h2>Meal Plan - {selectedMealPlan.client_name}</h2>
              <div className="header-actions">
                {isFeatureAvailable('meal_templates') && (
                  <button 
                    className="btn-secondary" 
                    onClick={() => {
                      setShowSaveTemplateModal(true);
                      if (templateCategories.length === 0) loadTemplateCategories();
                    }} 
                    disabled={loading}
                  >
                    <Download size={16} /> Save as Template
                  </button>
                )}
                <button className="btn-secondary" onClick={openDuplicateModal} disabled={loading}>
                  <ClipboardList size={16} /> Duplicate
                </button>
                <button className="btn-primary" onClick={() => openPdfPreview(selectedMealPlan)} disabled={loading} data-testid="export-pdf-btn">
                  {loading ? 'Exporting...' : <><FileText size={16} /> Export PDF</>}
                </button>
                <button className="btn-back" onClick={() => setCurrentView('client-detail')}><ArrowLeft size={16} /> Back</button>
              </div>
            </div>

            <div className="plan-summary-with-chart">
              <div className="plan-summary">
                <div className="summary-item">
                  <span className="label">Daily Calories</span>
                  <span className="value">{selectedMealPlan.macro_totals?.calories || selectedMealPlan.target_calories}</span>
                </div>
                <div className="summary-item">
                  <span className="label">Macros</span>
                  <span className="value">
                    P: {(() => { const mt = selectedMealPlan.macro_totals; const ms = selectedMealPlan.macro_split; const cal = mt?.calories || selectedMealPlan.target_calories || 2000; const p = mt?.protein || ms?.protein || 30; return mt?.protein_grams || Math.round(p / 100 * cal / 4); })()}g ({selectedMealPlan.macro_totals?.protein || selectedMealPlan.macro_split?.protein}%) | 
                    C: {(() => { const mt = selectedMealPlan.macro_totals; const ms = selectedMealPlan.macro_split; const cal = mt?.calories || selectedMealPlan.target_calories || 2000; const c = mt?.carbohydrates || ms?.carbohydrates || 40; return mt?.carbs_grams || Math.round(c / 100 * cal / 4); })()}g ({selectedMealPlan.macro_totals?.carbohydrates || selectedMealPlan.macro_split?.carbohydrates}%) | 
                    F: {(() => { const mt = selectedMealPlan.macro_totals; const ms = selectedMealPlan.macro_split; const cal = mt?.calories || selectedMealPlan.target_calories || 2000; const f = mt?.fat || ms?.fat || 30; return mt?.fat_grams || Math.round(f / 100 * cal / 9); })()}g ({selectedMealPlan.macro_totals?.fat || selectedMealPlan.macro_split?.fat}%)
                  </span>
                </div>
                {subscription?.plan === 'starter' && (
                  <div className="summary-item swap-limit">
                    <span className="label">Meal Swaps</span>
                    <span className="value">
                      {selectedMealPlan.swap_count || 0} / 2 used
                      {(selectedMealPlan.swap_count || 0) >= 2 && (
                        <span className="upgrade-link" onClick={() => setCurrentView('billing')}>
                          Upgrade for unlimited
                        </span>
                      )}
                    </span>
                  </div>
                )}
              </div>
              <div className="plan-macro-chart">
                <MacroPieChart
                  protein={selectedMealPlan.macro_totals?.protein || selectedMealPlan.macro_split?.protein || 30}
                  carbs={selectedMealPlan.macro_totals?.carbohydrates || selectedMealPlan.macro_split?.carbohydrates || 40}
                  fat={selectedMealPlan.macro_totals?.fat || selectedMealPlan.macro_split?.fat || 30}
                  proteinGrams={selectedMealPlan.macro_totals?.protein_grams}
                  carbsGrams={selectedMealPlan.macro_totals?.carbs_grams}
                  fatGrams={selectedMealPlan.macro_totals?.fat_grams}
                  calories={selectedMealPlan.macro_totals?.calories || selectedMealPlan.target_calories}
                  size={130}
                  showLegend={false}
                />
              </div>
            </div>

            <div className="meal-samples">
              {Object.entries(selectedMealPlan.daily_meals || {}).map(([sampleKey, sampleData]) => (
                <div key={sampleKey} className="sample-card">
                  <h3>Option {sampleKey.replace('sample_', '')}</h3>
                  <div className="meals-grid">
                    {Object.entries(sampleData || {}).map(([mealType, mealData]) => (
                      <div key={mealType} className="meal-card">
                        <div className="meal-header">
                          <h4>{mealType.replace('_', ' ')}</h4>
                          <div className="meal-actions">
                            <button
                              className="btn-icon-action"
                              onClick={() => openMealEditor(sampleKey, mealType, mealData)}
                              title="Edit meal"
                            >
                              <Edit size={14} />
                            </button>
                            <button
                              className="btn-swap"
                              onClick={() => handleSwapMeal(selectedMealPlan.id, sampleKey, mealType)}
                              disabled={swappingMeal === `${sampleKey}-${mealType}` || 
                                (subscription?.plan === 'starter' && (selectedMealPlan.swap_count || 0) >= 2)}
                              title={subscription?.plan === 'starter' && (selectedMealPlan.swap_count || 0) >= 2 
                                ? 'Upgrade to Pro for unlimited swaps' : 'Swap this meal'}
                            >
                              {swappingMeal === `${sampleKey}-${mealType}` ? '...' : <RefreshCw size={14} />}
                            </button>
                            <button
                              className="btn-icon-action btn-custom-request"
                              onClick={() => {
                                setCustomMealModal({ sampleKey, mealType });
                                setCustomMealPrompt('');
                              }}
                              disabled={subscription?.plan === 'starter' && (selectedMealPlan.swap_count || 0) >= 2}
                              title="Custom request - describe what you want"
                            >
                              <MessageSquare size={14} />
                            </button>
                            <button
                              className="btn-icon-action btn-icon-danger"
                              onClick={() => handleDeleteMeal(sampleKey, mealType)}
                              title="Delete meal"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                        <p className="meal-name">{mealData?.name}</p>
                        <div className="meal-macros">
                          <span>{mealData?.calories} kcal</span>
                          <span>P:{mealData?.protein}g</span>
                          <span>C:{mealData?.carbs}g</span>
                          <span>F:{mealData?.fat}g</span>
                        </div>
                        {mealData?.ingredients && (
                          <div className="meal-ingredients">
                            <strong>Ingredients:</strong>
                            <ul>
                              {mealData.ingredients.map((ing, idx) => (
                                <li key={idx}>{ing.name} - {ing.quantity}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {mealData?.method && mealData.method.length > 0 && (
                          <div className="meal-method" data-testid={`meal-method-${mealType}`}>
                            <strong>Method:</strong>
                            <ol>
                              {mealData.method.map((step, idx) => (
                                <li key={idx}>{step}</li>
                              ))}
                            </ol>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Shopping List */}
            {selectedMealPlan.shopping_list && Object.keys(selectedMealPlan.shopping_list).length > 0 ? (
              <div className="shopping-list-section">
                <h3><ShoppingCart size={20} style={{marginRight: '8px'}} />Shopping List</h3>
                <div className="shopping-categories">
                  {Object.entries(selectedMealPlan.shopping_list).map(([category, items]) => (
                    <div key={category} className="category">
                      <h4>{category}</h4>
                      <ul>
                        {items.map((item, idx) => (
                          <li key={idx}>{item.name} - {item.quantity}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            ) : !isFeatureAvailable('shopping_list') && (
              <div className="shopping-list-section feature-locked-section">
                <h3>Shopping List</h3>
                <div className="feature-locked-content">
                  <span className="lock-icon"><Lock size={24} /></span>
                  <p>Auto-generated shopping lists are available on Pro and Unlimited plans.</p>
                  <button className="btn-upgrade" onClick={() => setCurrentView('billing')}>
                    Upgrade to Pro
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Quick Meal Plan View */}
        {currentView === 'quick-meal-plan' && (
          <div className="quick-meal-plan-view">
            <div className="page-header">
              <h2>Quick Meal Plan Generator</h2>
              <button className="btn-back" onClick={() => setCurrentView('home')}><ArrowLeft size={16} /> Back</button>
            </div>

            <div className="quick-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Daily Calories *</label>
                  <input
                    type="number"
                    value={quickMealData.calories}
                    onChange={(e) => setQuickMealData({ ...quickMealData, calories: e.target.value })}
                    placeholder="e.g., 2000"
                    min="800"
                    max="6000"
                  />
                </div>
                <div className="form-group">
                  <label>Number of Meals</label>
                  <select
                    value={quickMealData.num_meals}
                    onChange={(e) => setQuickMealData({ ...quickMealData, num_meals: parseInt(e.target.value) })}
                  >
                    {[2, 3, 4, 5, 6].map(n => (
                      <option key={n} value={n}>{n} meals</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Protein %</label>
                  <input
                    type="number"
                    value={quickMealData.protein_pct}
                    onChange={(e) => setQuickMealData({ ...quickMealData, protein_pct: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Carbs %</label>
                  <input
                    type="number"
                    value={quickMealData.carbs_pct}
                    onChange={(e) => setQuickMealData({ ...quickMealData, carbs_pct: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Fat %</label>
                  <input
                    type="number"
                    value={quickMealData.fat_pct}
                    onChange={(e) => setQuickMealData({ ...quickMealData, fat_pct: e.target.value })}
                  />
                </div>
              </div>
              <button 
                className="btn-primary"
                onClick={handleGenerateQuickMealPlan}
                disabled={loading || generatingMealPlan || !quickMealData.calories}
              >
                {generatingMealPlan ? <><Brain size={16} /> Generating...</> : <><Zap size={16} /> Generate Quick Plan</>}
              </button>
              
              {/* Quick Meal Progress */}
              {generatingMealPlan && (
                <div className="generation-progress-card quick-meal-progress">
                  <div className="generation-header">
                    <span className="generation-icon"><Brain size={20} /></span>
                    <span className="generation-title">Plate Planner Pro is creating your meal plan</span>
                  </div>
                  <div className="generation-step">{generationStep}</div>
                  <div className="progress-bar-container">
                    <div 
                      className="progress-bar-fill" 
                      style={{ width: `${generationProgress}%` }}
                    />
                  </div>
                  <div className="progress-percentage">{generationProgress}%</div>
                </div>
              )}
            </div>

            {quickMealResult && (
              <div className="quick-result">
                <div className="quick-result-header">
                  <h3>Generated Meal Plan</h3>
                  <button 
                    className="btn-primary"
                    onClick={handleExportQuickMealPDF}
                    disabled={loading}
                    data-testid="quick-meal-download-btn"
                  >
                    {loading ? 'Downloading...' : <><FileDown size={16} /> Download PDF</>}
                  </button>
                </div>
                <div className="meal-samples">
                  {Object.entries(quickMealResult.daily_meals || {}).map(([sampleKey, sampleData]) => (
                    <div key={sampleKey} className="sample-card">
                      <h4>Option {sampleKey.replace('sample_', '')}</h4>
                      <div className="meals-grid">
                        {Object.entries(sampleData || {}).map(([mealType, mealData]) => (
                          <div key={mealType} className="meal-card">
                            <h5>{mealType}</h5>
                            <p className="meal-name">{mealData?.name}</p>
                            <div className="meal-macros">
                              <span>{mealData?.calories} kcal</span>
                              <span>P:{mealData?.protein}g</span>
                              <span>C:{mealData?.carbs}g</span>
                              <span>F:{mealData?.fat}g</span>
                            </div>
                            {mealData?.ingredients && (
                              <div className="meal-ingredients">
                                <strong>Ingredients:</strong>
                                <ul>
                                  {mealData.ingredients.map((ing, idx) => (
                                    <li key={idx}>{ing.name} - {ing.quantity}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {mealData?.method && mealData.method.length > 0 && (
                              <div className="meal-method">
                                <strong>Method:</strong>
                                <ol>
                                  {mealData.method.map((step, idx) => (
                                    <li key={idx}>{step}</li>
                                  ))}
                                </ol>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Templates View (Unlimited plan) */}
        {currentView === 'templates' && (
          <div className="templates-view">
            <div className="page-header">
              <h2><ClipboardList size={24} style={{marginRight: '8px'}} />Meal Plan Templates</h2>
              <button className="btn-back" onClick={() => setCurrentView('home')}><ArrowLeft size={16} /> Back</button>
            </div>

            <p className="section-description">
              Save your best meal plans as templates to quickly apply them to new clients.
            </p>

            {/* Category Filter */}
            <div className="template-filters">
              <label>Filter by category:</label>
              <select 
                value={selectedTemplateCategory} 
                onChange={(e) => {
                  setSelectedTemplateCategory(e.target.value);
                  loadTemplates(e.target.value);
                }}
              >
                <option value="">All Categories</option>
                {templateCategories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            {templates.length === 0 ? (
              <div className="empty-state">
                <p>No templates yet. Create a meal plan and save it as a template!</p>
                <button className="btn-primary" onClick={() => setCurrentView('clients')}>
                  View Clients
                </button>
              </div>
            ) : (
              <div className="templates-grid">
                {templates.map(template => (
                  <div key={template.template_id} className="template-card">
                    <div className="template-header">
                      <h3>{template.name}</h3>
                      <span className="template-category">{template.category}</span>
                    </div>
                    {template.description && (
                      <p className="template-description">{template.description}</p>
                    )}
                    <div className="template-meta">
                      <span><Flame size={14} /> {template.target_calories} kcal</span>
                      <span><BarChart3 size={14} /> Used {template.usage_count || 0} times</span>
                    </div>
                    <div className="template-actions">
                      <button 
                        className="btn-primary btn-sm"
                        onClick={() => {
                          setSelectedTemplateToApply(template);
                          setShowApplyTemplateModal(true);
                          if (clients.length === 0) loadClients();
                        }}
                      >
                        Apply to Client
                      </button>
                      <button 
                        className="btn-danger btn-sm"
                        onClick={() => handleDeleteTemplate(template.template_id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Help View */}
        {currentView === 'help' && (
          <HelpPage
            setCurrentView={setCurrentView}
            subscription={subscription}
          />
        )}

        {/* Admin Dashboard View */}
        {currentView === 'admin' && isAdmin() && (
          <div className="admin-view">
            <div className="page-header">
              <h2><Shield size={24} style={{marginRight: '8px'}} />Admin Dashboard</h2>
              <button className="btn-back" onClick={() => setCurrentView('home')}><ArrowLeft size={16} /> Back</button>
            </div>

            {/* Stats Overview */}
            {adminStats && (
              <div className="admin-stats-grid">
                <div 
                  className={`admin-stat-card clickable ${adminFilters.status === '' && adminFilters.plan === '' ? 'active-filter' : ''}`}
                  onClick={() => { const newFilters = {plan: '', status: '', search: ''}; setAdminFilters(newFilters); loadAdminUsers(1, newFilters); }}
                  title="Show all users"
                >
                  <div className="stat-icon"><Users size={20} /></div>
                  <div className="stat-content">
                    <span className="stat-value">{adminStats.total_users}</span>
                    <span className="stat-label">Total Users</span>
                  </div>
                </div>
                <div className="admin-stat-card highlight">
                  <div className="stat-icon"><DollarSign size={20} /></div>
                  <div className="stat-content">
                    <span className="stat-value">£{adminStats.estimated_mrr}</span>
                    <span className="stat-label">Est. MRR</span>
                  </div>
                </div>
                <div 
                  className={`admin-stat-card clickable ${adminFilters.status === 'active' ? 'active-filter' : ''}`}
                  onClick={() => { const newFilters = {...adminFilters, status: 'active', plan: ''}; setAdminFilters(newFilters); loadAdminUsers(1, newFilters); }}
                  title="Filter: Active paying users"
                >
                  <div className="stat-icon"><Check size={20} /></div>
                  <div className="stat-content">
                    <span className="stat-value">{adminStats.active_paying_users}</span>
                    <span className="stat-label">Active Paying</span>
                  </div>
                </div>
                <div 
                  className={`admin-stat-card clickable ${adminFilters.status === 'trialing' ? 'active-filter' : ''}`}
                  onClick={() => { const newFilters = {...adminFilters, status: 'trialing', plan: ''}; setAdminFilters(newFilters); loadAdminUsers(1, newFilters); }}
                  title="Filter: Users on trial"
                >
                  <div className="stat-icon"><CalendarDays size={20} /></div>
                  <div className="stat-content">
                    <span className="stat-value">{adminStats.users_by_status.trialing || 0}</span>
                    <span className="stat-label">On Trial</span>
                  </div>
                </div>
                <div 
                  className={`admin-stat-card clickable ${adminFilters.status === 'past_due' ? 'active-filter' : ''}`}
                  onClick={() => { const newFilters = {...adminFilters, status: 'past_due', plan: ''}; setAdminFilters(newFilters); loadAdminUsers(1, newFilters); }}
                  title="Filter: Payment failed"
                >
                  <div className="stat-icon"><AlertTriangle size={20} /></div>
                  <div className="stat-content">
                    <span className="stat-value">{adminStats.users_by_status.past_due || 0}</span>
                    <span className="stat-label">Past Due</span>
                  </div>
                </div>
                <div 
                  className={`admin-stat-card clickable ${adminFilters.status === 'canceled' ? 'active-filter' : ''}`}
                  onClick={() => { const newFilters = {...adminFilters, status: 'canceled', plan: ''}; setAdminFilters(newFilters); loadAdminUsers(1, newFilters); }}
                  title="Filter: Canceled users"
                >
                  <div className="stat-icon"><XCircle size={20} /></div>
                  <div className="stat-content">
                    <span className="stat-value">{adminStats.users_by_status.canceled || 0}</span>
                    <span className="stat-label">Canceled</span>
                  </div>
                </div>
              </div>
            )}

            {/* Plans Breakdown */}
            {adminStats && (
              <div className="admin-section">
                <h3>Users by Plan</h3>
                <div className="plan-breakdown">
                  <div className="plan-bar-item">
                    <span className="plan-name">Starter</span>
                    <div className="plan-bar">
                      <div 
                        className="plan-bar-fill starter" 
                        style={{width: `${(adminStats.users_by_plan.starter / adminStats.total_users) * 100}%`}}
                      />
                    </div>
                    <span className="plan-count">{adminStats.users_by_plan.starter}</span>
                  </div>
                  <div className="plan-bar-item">
                    <span className="plan-name">Pro</span>
                    <div className="plan-bar">
                      <div 
                        className="plan-bar-fill pro" 
                        style={{width: `${(adminStats.users_by_plan.pro / adminStats.total_users) * 100}%`}}
                      />
                    </div>
                    <span className="plan-count">{adminStats.users_by_plan.pro}</span>
                  </div>
                  <div className="plan-bar-item">
                    <span className="plan-name">Unlimited</span>
                    <div className="plan-bar">
                      <div 
                        className="plan-bar-fill unlimited" 
                        style={{width: `${(adminStats.users_by_plan.unlimited / adminStats.total_users) * 100}%`}}
                      />
                    </div>
                    <span className="plan-count">{adminStats.users_by_plan.unlimited}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Status Breakdown */}
            {adminStats && (
              <div className="admin-section">
                <h3>Users by Status</h3>
                <div className="status-pills">
                  <span className="status-pill trialing">
                    Trialing: {adminStats.users_by_status.trialing || 0}
                  </span>
                  <span className="status-pill active">
                    Active: {adminStats.users_by_status.active || 0}
                  </span>
                  <span className="status-pill expired">
                    Expired: {adminStats.users_by_status.expired || 0}
                  </span>
                  <span className="status-pill canceled">
                    Canceled: {adminStats.users_by_status.canceled || 0}
                  </span>
                </div>
              </div>
            )}

            {/* User Management */}
            <div className="admin-section">
              <h3>User Management</h3>
              
              {/* Filters */}
              <div className="admin-filters">
                <input
                  type="text"
                  placeholder="Search by email or name..."
                  value={adminFilters.search}
                  onChange={(e) => setAdminFilters({...adminFilters, search: e.target.value})}
                  onKeyPress={(e) => e.key === 'Enter' && loadAdminUsers(1)}
                />
                <select
                  value={adminFilters.plan}
                  onChange={(e) => {
                    const newFilters = {...adminFilters, plan: e.target.value};
                    setAdminFilters(newFilters);
                    loadAdminUsers(1, newFilters);
                  }}
                >
                  <option value="">All Plans</option>
                  <option value="starter">Starter</option>
                  <option value="pro">Pro</option>
                  <option value="unlimited">Unlimited</option>
                </select>
                <select
                  value={adminFilters.status}
                  onChange={(e) => {
                    const newFilters = {...adminFilters, status: e.target.value};
                    setAdminFilters(newFilters);
                    loadAdminUsers(1, newFilters);
                  }}
                >
                  <option value="">All Status</option>
                  <option value="trialing">Trialing</option>
                  <option value="active">Active</option>
                  <option value="expired">Expired</option>
                  <option value="canceled">Canceled</option>
                </select>
                <button className="btn-primary btn-sm" onClick={() => loadAdminUsers(1)}>
                  Search
                </button>
              </div>

              {/* Users Table */}
              {adminLoading ? (
                <div className="loading-state">Loading users...</div>
              ) : (
                <div className="admin-table-wrapper">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>User</th>
                        <th>Plan</th>
                        <th>Status</th>
                        <th>Clients</th>
                        <th>Joined</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {adminUsers.map(u => (
                        <tr key={u.user_id} className={u.is_admin ? 'admin-row' : ''}>
                          <td>
                            <div className="user-cell">
                              <span className="user-name">{u.name}</span>
                              <span className="user-email">{u.email}</span>
                              {u.is_admin && <span className="admin-badge">Admin</span>}
                              {!u.email_verified && <span className="unverified-badge">Unverified</span>}
                            </div>
                          </td>
                          <td>
                            <span className={`plan-badge plan-${u.plan}`}>{u.plan}</span>
                          </td>
                          <td>
                            <span className={`status-badge status-${u.plan_status}`}>{u.plan_status}</span>
                          </td>
                          <td>{u.clients_count || 0}</td>
                          <td>{new Date(u.created_at).toLocaleDateString()}</td>
                          <td>
                            <div className="action-buttons">
                              <button 
                                className="btn-sm btn-secondary"
                                onClick={() => loadAdminUserDetail(u.user_id)}
                              >
                                View
                              </button>
                              {!u.is_admin && (
                                <button 
                                  className="btn-sm btn-danger"
                                  onClick={() => handleAdminDeleteUser(u.user_id)}
                                  title="Delete user"
                                >
                                  Delete
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              {adminPagination.total_pages > 1 && (
                <div className="admin-pagination">
                  <button 
                    className="btn-sm"
                    disabled={adminPagination.page === 1}
                    onClick={() => loadAdminUsers(adminPagination.page - 1)}
                  >
                    Previous
                  </button>
                  <span>Page {adminPagination.page} of {adminPagination.total_pages}</span>
                  <button 
                    className="btn-sm"
                    disabled={adminPagination.page === adminPagination.total_pages}
                    onClick={() => loadAdminUsers(adminPagination.page + 1)}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>

            {/* User Detail Modal */}
            {selectedAdminUser && (
              <div className="modal-overlay" onClick={() => setSelectedAdminUser(null)}>
                <div className="admin-user-modal" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-header">
                    <h3>User Details</h3>
                    <button className="close-btn" onClick={() => setSelectedAdminUser(null)}>×</button>
                  </div>
                  
                  <div className="user-detail-content">
                    <div className="user-detail-header">
                      <div>
                        <h4>{selectedAdminUser.name}</h4>
                        <p>{selectedAdminUser.email}</p>
                        {selectedAdminUser.is_admin && <span className="admin-badge">Admin</span>}
                      </div>
                      <div className="user-plan-status">
                        <span className={`plan-badge plan-${selectedAdminUser.plan}`}>{selectedAdminUser.plan}</span>
                        <span className={`status-badge status-${selectedAdminUser.plan_status}`}>{selectedAdminUser.plan_status}</span>
                      </div>
                    </div>

                    <div className="user-stats-row">
                      <div className="user-stat">
                        <span className="label">Clients</span>
                        <span className="value">{selectedAdminUser.clients_count || 0}</span>
                      </div>
                      <div className="user-stat">
                        <span className="label">Meal Plans</span>
                        <span className="value">{selectedAdminUser.meal_plans_count || 0}</span>
                      </div>
                      <div className="user-stat">
                        <span className="label">Joined</span>
                        <span className="value">{new Date(selectedAdminUser.created_at).toLocaleDateString()}</span>
                      </div>
                      <div className="user-stat">
                        <span className="label">Email Verified</span>
                        <span className="value">{selectedAdminUser.email_verified ? <><Check size={14} /> Yes</> : <><XCircle size={14} /> No</>}</span>
                      </div>
                    </div>

                    {/* Plan Management */}
                    <div className="admin-action-section">
                      <h5><Gift size={16} style={{marginRight: '6px'}} />Gift Free Plan Upgrade</h5>
                      <p className="admin-hint">Upgrade users without charging them - bypasses Stripe</p>
                      <div className="plan-change-buttons">
                        <button 
                          className={`btn-sm ${selectedAdminUser.plan === 'starter' ? 'btn-primary' : 'btn-secondary'}`}
                          onClick={() => handleAdminUpdatePlan(selectedAdminUser.user_id, 'starter', 'active')}
                        >
                          <Gift size={12} /> Gift Starter
                        </button>
                        <button 
                          className={`btn-sm ${selectedAdminUser.plan === 'pro' ? 'btn-primary' : 'btn-secondary'}`}
                          onClick={() => handleAdminUpdatePlan(selectedAdminUser.user_id, 'pro', 'active')}
                        >
                          <Gift size={12} /> Gift Pro
                        </button>
                        <button 
                          className={`btn-sm ${selectedAdminUser.plan === 'unlimited' ? 'btn-primary' : 'btn-secondary'}`}
                          onClick={() => handleAdminUpdatePlan(selectedAdminUser.user_id, 'unlimited', 'active')}
                        >
                          <Gift size={12} /> Gift Unlimited
                        </button>
                      </div>
                    </div>

                    {/* Admin Toggle */}
                    <div className="admin-action-section">
                      <h5>Admin Privileges</h5>
                      <button 
                        className={selectedAdminUser.is_admin ? 'btn-warning btn-sm' : 'btn-secondary btn-sm'}
                        onClick={() => handleAdminSetAdmin(selectedAdminUser.user_id, !selectedAdminUser.is_admin)}
                      >
                        {selectedAdminUser.is_admin ? 'Revoke Admin' : 'Grant Admin'}
                      </button>
                    </div>

                    {/* Recent Clients */}
                    {selectedAdminUser.recent_clients?.length > 0 && (
                      <div className="admin-action-section">
                        <h5>Recent Clients</h5>
                        <ul className="recent-list">
                          {selectedAdminUser.recent_clients.map(c => (
                            <li key={c.client_id}>{c.first_name} {c.last_name} - {c.email}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Payment History */}
                    {selectedAdminUser.payment_history?.length > 0 && (
                      <div className="admin-action-section">
                        <h5>Payment History</h5>
                        <ul className="recent-list">
                          {selectedAdminUser.payment_history.map(p => (
                            <li key={p.transaction_id}>
                              £{p.amount} - {p.plan} - {p.payment_status} - {new Date(p.created_at).toLocaleDateString()}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Danger Zone */}
                    <div className="admin-action-section danger-zone">
                      <h5>Danger Zone</h5>
                      <button 
                        className="btn-danger btn-sm"
                        onClick={() => handleAdminDeleteUser(selectedAdminUser.user_id)}
                      >
                        Delete User & All Data
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Confirmation Modal */}
      {confirmModal.show && (
        <div className="confirm-overlay" onClick={hideConfirm}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{confirmModal.title}</h3>
            <p>{confirmModal.message}</p>
            <div className="confirm-actions">
              <button className="btn-secondary" onClick={hideConfirm}>Cancel</button>
              <button className="btn-danger" onClick={confirmModal.onConfirm}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Duplicate Modal */}
      {/* PDF Preview Modal */}
      {showPdfPreview && pdfPreviewPlan && (
        <div className="modal-overlay" onClick={() => setShowPdfPreview(false)}>
          <div className="pdf-preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pdf-preview-header">
              <h3><FileText size={14} /> PDF Preview</h3>
              <button className="close-btn" onClick={() => setShowPdfPreview(false)}>×</button>
            </div>
            
            <div className="pdf-preview-content">
              {/* Branding Preview */}
              <div className="pdf-preview-branding" style={{borderTopColor: user?.brand_primary_color || '#021b79'}}>
                {user?.logo_url ? (
                  <img src={`${API_URL}${user.logo_url}`} alt="Logo" className="pdf-preview-logo" />
                ) : (
                  <div className="pdf-preview-company-name" style={{color: user?.brand_primary_color || '#021b79'}}>
                    {user?.company_name || 'PLATE PLANNER PRO'}
                  </div>
                )}
              </div>
              
              {/* Title */}
              <h2 className="pdf-preview-title" style={{color: user?.brand_primary_color || '#021b79'}}>
                Meal Plan for {pdfPreviewPlan.client_name}
              </h2>
              <p className="pdf-preview-date">
                Week Starting: {new Date(pdfPreviewPlan.week_starting || Date.now()).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </p>
              
              {/* Macros Summary */}
              <div className="pdf-preview-macros">
                <div className="pdf-macro-item" style={{backgroundColor: user?.brand_primary_color || '#021b79'}}>
                  <span className="macro-label">Daily Target</span>
                  <span className="macro-value">{pdfPreviewPlan.macro_totals?.calories || 'N/A'} kcal</span>
                </div>
                <div className="pdf-macro-item">
                  <span className="macro-label">Protein</span>
                  <span className="macro-value">{pdfPreviewPlan.macro_totals?.protein || 'N/A'}%</span>
                </div>
                <div className="pdf-macro-item">
                  <span className="macro-label">Carbs</span>
                  <span className="macro-value">{pdfPreviewPlan.macro_totals?.carbohydrates || 'N/A'}%</span>
                </div>
                <div className="pdf-macro-item">
                  <span className="macro-label">Fat</span>
                  <span className="macro-value">{pdfPreviewPlan.macro_totals?.fat || 'N/A'}%</span>
                </div>
              </div>
              
              {/* Sample Meals Preview */}
              <div className="pdf-preview-meals">
                {pdfPreviewPlan.daily_meals && Object.entries(pdfPreviewPlan.daily_meals).slice(0, 1).map(([sampleKey, meals]) => (
                  <div key={sampleKey} className="pdf-preview-sample">
                    <h4>{sampleKey.replace('sample_', 'Option ')}</h4>
                    {meals && Object.entries(meals).map(([mealType, mealData]) => (
                      mealData && typeof mealData === 'object' && (
                        <div key={mealType} className="pdf-preview-meal">
                          <strong>{mealType.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}:</strong>{' '}
                          {mealData.name} ({mealData.calories} kcal)
                        </div>
                      )
                    ))}
                  </div>
                ))}
                <p className="pdf-preview-more">+ {Object.keys(pdfPreviewPlan.daily_meals || {}).length - 1} more meal options...</p>
              </div>
              
              {/* Shopping List Indicator */}
              {pdfPreviewPlan.shopping_list && Object.keys(pdfPreviewPlan.shopping_list).length > 0 && (
                <div className="pdf-preview-shopping">
                  <span><ShoppingCart size={14} style={{marginRight: '6px'}} />Includes shopping list ({Object.keys(pdfPreviewPlan.shopping_list).length} categories)</span>
                </div>
              )}
            </div>
            
            <div className="pdf-preview-actions">
              <button className="btn-secondary" onClick={() => setShowPdfPreview(false)}>Cancel</button>
              <button className="btn-primary" onClick={() => handleExportPDF(pdfPreviewPlan.id)} disabled={loading}>
                {loading ? 'Downloading...' : '⬇️ Download PDF'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDuplicateModal && (
        <div className="confirm-overlay" onClick={() => setShowDuplicateModal(false)}>
          <div className="duplicate-modal" onClick={(e) => e.stopPropagation()}>
            <h3><Copy size={20} style={{marginRight: '8px'}} />Duplicate Meal Plan</h3>
            <p>Create a copy of this meal plan. You can assign it to the same client or a different one.</p>
            
            <div className="duplicate-form">
              <label>Select Client</label>
              <select 
                value={duplicateTargetClient} 
                onChange={(e) => setDuplicateTargetClient(e.target.value)}
              >
                {clients.map(client => (
                  <option key={client.id} value={client.id}>
                    {client.first_name} {client.last_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="confirm-actions">
              <button className="btn-secondary" onClick={() => setShowDuplicateModal(false)}>Cancel</button>
              <button className="btn-primary" onClick={handleDuplicateMealPlan} disabled={loading}>
                {loading ? 'Duplicating...' : 'Duplicate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Save Template Modal */}
      {showSaveTemplateModal && selectedMealPlan && (
        <div className="modal-overlay" onClick={() => setShowSaveTemplateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3><Download size={20} style={{marginRight: '8px'}} />Save as Template</h3>
            <p>Save this meal plan as a reusable template.</p>
            
            <div className="form-group">
              <label>Template Name *</label>
              <input
                type="text"
                value={newTemplateForm.name}
                onChange={(e) => setNewTemplateForm({ ...newTemplateForm, name: e.target.value })}
                placeholder="e.g., High Protein Cut"
              />
            </div>
            
            <div className="form-group">
              <label>Category *</label>
              <select
                value={newTemplateForm.category}
                onChange={(e) => setNewTemplateForm({ ...newTemplateForm, category: e.target.value })}
              >
                {templateCategories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            
            <div className="form-group">
              <label>Description (optional)</label>
              <textarea
                value={newTemplateForm.description}
                onChange={(e) => setNewTemplateForm({ ...newTemplateForm, description: e.target.value })}
                placeholder="Brief description of this meal plan..."
                rows={3}
              />
            </div>
            
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowSaveTemplateModal(false)}>Cancel</button>
              <button 
                className="btn-primary" 
                onClick={handleSaveTemplate} 
                disabled={loading || !newTemplateForm.name}
              >
                {loading ? 'Saving...' : 'Save Template'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Apply Template Modal */}
      {showApplyTemplateModal && selectedTemplateToApply && (
        <div className="modal-overlay" onClick={() => setShowApplyTemplateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3><ClipboardList size={20} style={{marginRight: '8px'}} />Apply Template</h3>
            <p>Apply "{selectedTemplateToApply.name}" to a client.</p>
            
            <div className="form-group">
              <label>Select Client *</label>
              <select
                value={duplicateTargetClient}
                onChange={(e) => setDuplicateTargetClient(e.target.value)}
              >
                <option value="">-- Select a client --</option>
                {clients.map(client => (
                  <option key={client.id} value={client.id}>
                    {client.first_name} {client.last_name}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowApplyTemplateModal(false)}>Cancel</button>
              <button 
                className="btn-primary" 
                onClick={() => handleApplyTemplate(selectedTemplateToApply.template_id, duplicateTargetClient)} 
                disabled={loading || !duplicateTargetClient}
              >
                {loading ? 'Applying...' : 'Apply Template'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invite Team Member Modal */}
      {showInviteModal && (
        <div className="modal-overlay" onClick={() => setShowInviteModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3><UserPlus size={20} style={{marginRight: '8px'}} />Add Team Member</h3>
            <p>Invite a team member to help manage clients and meal plans.</p>
            
            <div className="form-group">
              <label>Name *</label>
              <input
                type="text"
                value={inviteForm.name}
                onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                placeholder="Team member's name"
              />
            </div>
            
            <div className="form-group">
              <label>Email *</label>
              <input
                type="email"
                value={inviteForm.email}
                onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                placeholder="team@example.com"
              />
            </div>
            
            <div className="form-group">
              <label>Password *</label>
              <input
                type="password"
                value={inviteForm.password}
                onChange={(e) => setInviteForm({ ...inviteForm, password: e.target.value })}
                placeholder="Set their password"
              />
              <p className="field-hint">Share this password with them to log in.</p>
            </div>
            
            <div className="team-member-access-note">
              <strong>Team member access:</strong>
              <ul>
                <li>✓ View all clients</li>
                <li>✓ Create meal plans</li>
                <li>✓ Export PDFs</li>
                <li>✗ Cannot access billing</li>
                <li>✗ Cannot change settings</li>
              </ul>
            </div>
            
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowInviteModal(false)}>Cancel</button>
              <button 
                className="btn-primary" 
                onClick={handleInviteTeamMember} 
                disabled={loading || !inviteForm.name || !inviteForm.email || !inviteForm.password}
              >
                {loading ? 'Adding...' : 'Add Team Member'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Meal Request Modal */}
      {customMealModal && (
        <div className="modal-overlay" onClick={() => { setCustomMealModal(null); setCustomMealPrompt(''); }}>
          <div className="modal-content custom-meal-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3><MessageSquare size={20} style={{marginRight: '8px'}} />Custom Request</h3>
              <button className="btn-close" onClick={() => { setCustomMealModal(null); setCustomMealPrompt(''); }}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <p className="modal-description">
                Describe what you want for this {customMealModal.mealType.replace('_', ' ')} and we'll create it with calculated macros.
              </p>
              <div className="form-group">
                <label>What would you like?</label>
                <textarea
                  value={customMealPrompt}
                  onChange={(e) => setCustomMealPrompt(e.target.value)}
                  placeholder="e.g., packet of crisps and some peanuts, or a chicken wrap with salad, or a protein shake with banana..."
                  rows={3}
                  autoFocus
                  data-testid="custom-meal-prompt"
                />
              </div>
              <div className="custom-meal-examples">
                <span className="examples-label">Examples:</span>
                <button type="button" className="example-chip" onClick={() => setCustomMealPrompt('packet of crisps and handful of peanuts')}>
                  Crisps & peanuts
                </button>
                <button type="button" className="example-chip" onClick={() => setCustomMealPrompt('protein shake with banana and peanut butter')}>
                  Protein shake
                </button>
                <button type="button" className="example-chip" onClick={() => setCustomMealPrompt('Greek yogurt with honey and berries')}>
                  Greek yogurt
                </button>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn-secondary" 
                onClick={() => { setCustomMealModal(null); setCustomMealPrompt(''); }}
              >
                Cancel
              </button>
              <button 
                className="btn-primary" 
                onClick={handleCustomMealRequest}
                disabled={submittingCustomMeal || !customMealPrompt.trim()}
                data-testid="custom-meal-submit"
              >
                {submittingCustomMeal ? (
                  <>
                    <RefreshCw size={14} className="spin" /> Creating...
                  </>
                ) : (
                  <>
                    <Sparkles size={14} /> Create Meal
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Meal Editor Modal */}
      {showMealEditor && (
        <div className="modal-overlay" onClick={() => setShowMealEditor(false)}>
          <div className="modal-content meal-editor-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3><Edit size={20} style={{marginRight: '8px'}} />Edit {editingMealData.meal_type.replace('_', ' ')}</h3>
              <button className="btn-close" onClick={() => setShowMealEditor(false)}>
                <X size={20} />
              </button>
            </div>
            
            <div className="meal-editor-form">
              <div className="form-group">
                <label>Meal Name</label>
                <input
                  type="text"
                  value={editingMealData.meal.name}
                  onChange={(e) => setEditingMealData(prev => ({
                    ...prev,
                    meal: { ...prev.meal, name: e.target.value }
                  }))}
                  placeholder="e.g., Grilled Chicken Salad"
                />
              </div>
              
              <div className="form-row macro-inputs">
                <div className="form-group">
                  <label><Flame size={14} /> Calories</label>
                  <input
                    type="number"
                    value={editingMealData.meal.calories}
                    onChange={(e) => setEditingMealData(prev => ({
                      ...prev,
                      meal: { ...prev.meal, calories: parseInt(e.target.value) || 0 }
                    }))}
                  />
                </div>
                <div className="form-group">
                  <label>Protein (g)</label>
                  <input
                    type="number"
                    value={editingMealData.meal.protein}
                    onChange={(e) => setEditingMealData(prev => ({
                      ...prev,
                      meal: { ...prev.meal, protein: parseInt(e.target.value) || 0 }
                    }))}
                  />
                </div>
                <div className="form-group">
                  <label>Carbs (g)</label>
                  <input
                    type="number"
                    value={editingMealData.meal.carbs}
                    onChange={(e) => setEditingMealData(prev => ({
                      ...prev,
                      meal: { ...prev.meal, carbs: parseInt(e.target.value) || 0 }
                    }))}
                  />
                </div>
                <div className="form-group">
                  <label>Fat (g)</label>
                  <input
                    type="number"
                    value={editingMealData.meal.fat}
                    onChange={(e) => setEditingMealData(prev => ({
                      ...prev,
                      meal: { ...prev.meal, fat: parseInt(e.target.value) || 0 }
                    }))}
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label>
                  Ingredients
                  <button type="button" className="btn-add-inline" onClick={handleAddIngredient}>
                    <Plus size={14} /> Add
                  </button>
                </label>
                <div className="ingredients-list">
                  {editingMealData.meal.ingredients.map((ing, idx) => (
                    <div key={idx} className="ingredient-row">
                      <input
                        type="text"
                        placeholder="Ingredient name"
                        value={ing.name}
                        onChange={(e) => handleIngredientChange(idx, 'name', e.target.value)}
                      />
                      <input
                        type="text"
                        placeholder="Quantity"
                        value={ing.quantity}
                        onChange={(e) => handleIngredientChange(idx, 'quantity', e.target.value)}
                      />
                      <button 
                        type="button" 
                        className="btn-remove-ingredient"
                        onClick={() => handleRemoveIngredient(idx)}
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                  {editingMealData.meal.ingredients.length === 0 && (
                    <p className="no-ingredients">No ingredients added. Click "Add" to add ingredients.</p>
                  )}
                </div>
              </div>
              
              <div className="form-group">
                <label>Instructions (optional)</label>
                <textarea
                  value={editingMealData.meal.instructions || ''}
                  onChange={(e) => setEditingMealData(prev => ({
                    ...prev,
                    meal: { ...prev.meal, instructions: e.target.value }
                  }))}
                  placeholder="Cooking instructions..."
                  rows={3}
                />
              </div>
            </div>
            
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowMealEditor(false)}>Cancel</button>
              <button 
                className="btn-primary" 
                onClick={handleSaveMeal}
                disabled={savingMeal || !editingMealData.meal.name}
              >
                {savingMeal ? 'Saving...' : 'Save Meal'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bug Report Button */}
      <button 
        className="bug-report-btn" 
        onClick={() => setShowBugReportModal(true)}
        data-testid="bug-report-btn"
      >
        <Bug size={18} />
        <span>Report Bug</span>
      </button>

      {/* Bug Report Modal */}
      <BugReportModal
        isOpen={showBugReportModal}
        onClose={() => setShowBugReportModal(false)}
        currentView={currentView}
        API_URL={API_URL}
      />

      <footer className="footer">
        <p>© 2026 Plate Planner Pro</p>
      </footer>
    </div>
  );
}

export default App;
